import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button,
  Grid,
  TextField,
  InputAdornment,
  ButtonGroup,
  Tooltip,
  Autocomplete,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  useTheme,
  useMediaQuery,
  TablePagination,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import InventoryIcon from '@mui/icons-material/Inventory';
import BoltIcon from '@mui/icons-material/Bolt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import RefreshIcon from '@mui/icons-material/Refresh';
import FilterListIcon from '@mui/icons-material/FilterList';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import CameraAltIcon from '@mui/icons-material/CameraAlt';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  INVENTORY_FILTERS_EVENT,
  consumePendingInventoryFilters,
  type InventoryFilterRequest,
} from '../../aichat/agent/uiBridge';
import type { 
  InventoryItem, 
  InventoryCategory, 
  Brand, 
  Warehouse, 
  Branch,
  WarehouseStockDto,
  TableColumn, 
  Order 
} from './types/inventory.types';
import { 
  INVENTORY_TABLE_SETTINGS_KEY, 
  HIDE_EMPTY_STOCK_KEY, 
  HIDE_INACTIVE_ITEMS_KEY,
  realtimeListenerActive,
  setRealtimeListenerActive
} from './types/inventory.types';
import { getCurrencySymbol, getTaxStatusText } from './utils/formatters';
import { getCellValue } from './utils/tableCellUtils';
import { useAuth } from '../../../contexts/AuthContext';
import { useInventoryData } from './hooks/useInventoryData';
import { useBulkOperations } from './hooks/useBulkOperations';
import { useInventoryValueUpdate } from './hooks/useInventoryValueUpdate';
import { useInventoryExcelExport } from './hooks/useInventoryExcelExport';
import { useN11ProductSend } from './hooks/useN11ProductSend';
import { useInventoryImageUpdate } from './hooks/useInventoryImageUpdate';
import { useInventoryItemActions } from './hooks/useInventoryItemActions';
import { useBulkOperationsHandlers } from './hooks/useBulkOperationsHandlers';
import { InventoryFilters, InventoryFilterBadge, type InventoryFilterDraft } from './components/InventoryFilters';
import { InventoryTable } from './components/InventoryTable';
import { InventoryMobileCard } from './components/InventoryMobileCard';
import { InventoryModals } from './components/InventoryModals';
import { MobilBulkActionButtons } from './components/MobilBulkActionButtons';
import { BarcodeScanner } from './components/BarcodeScanner';
import ImageViewModal from './components/ImageViewModal';
import { BranchPriceModal } from './components/BranchPriceModal';
import { RecipeModal } from './components/RecipeModal';
import { ProductMenuModal } from './components/ProductMenuModal';
import { VariantManagementModal } from './components/VariantManagementModal';
import ImageActionMenu from '../../common/ImageActionMenu';
import ConfirmDialog from '../../common/ConfirmDialog';
import { InventoryService, IntegrationService, EcommerceService, N11Service } from './services';
import { WarehouseService } from './services/warehouseService';
import { setRealtimeListenerToggle } from './InventoryImportModal';
import { ACCOUNTING_API } from '../../../config/api';
import { accountingApi, mainApi } from '../../../config/apiClient';
import { StatsCardItem, StatsCardsContainer, STATS_COLORS, formatStatsCurrency } from '../shared/StatsCard';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RemoveShoppingCartIcon from '@mui/icons-material/RemoveShoppingCart';
import CategoryIcon from '@mui/icons-material/Category';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import SellIcon from '@mui/icons-material/Sell';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';

const Inventory: React.FC = () => {
  const { t } = useTranslation('accounting');
  const UNIT_TRANSLATION_KEYS: Record<string, string> = {
    adet: 'piece',
    kg: 'kg',
    gr: 'gram',
    lt: 'liter',
    ml: 'milliliter',
    m: 'meter',
    cm: 'centimeter',
    'm²': 'squareMeter',
    'm³': 'cubicMeter',
    'çift': 'pair',
    paket: 'pack',
    kutu: 'box',
    top: 'rollBundle',
    rulo: 'roll',
  };
  const localizeUnit = (unitValue?: string | null): string => {
    if (!unitValue) return '';
    const key = UNIT_TRANSLATION_KEYS[unitValue];
    return key ? t(`stock.addModal.units.${key}`) : unitValue;
  };
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [companyId, setCompanyId] = useState<string | null>(null);
  
  // Kategori filtresi için state
  const [selectedCategory, setSelectedCategory] = useState<InventoryCategory | null>(null);
  
  // Marka filtresi için state
  const [selectedBrand, setSelectedBrand] = useState<Brand | null>(null);
  
  // Depo filtresi için state
  const [selectedWarehouse, setSelectedWarehouse] = useState<Warehouse | null>(null);
  
  // Şube filtresi için state
  const [selectedBranch, setSelectedBranch] = useState<Branch | null>(null);
  
  // KDV Oranı filtresi için state
  const [selectedVatRate, setSelectedVatRate] = useState<number | null>(null);
  
  // ÖKC (Departman No) filtresi için state
  const [selectedDepartmentNo, setSelectedDepartmentNo] = useState<number | null>(null);
  
  // WMS lokasyon filtresi (akıllı depo)
  const [locationOptions, setLocationOptions] = useState<{ id: string; code: string; name: string }[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<{ id: string; code: string; name: string } | null>(null);

  // ✅ Gelişmiş filtreler
  const [minSalePrice, setMinSalePrice] = useState<number | null>(null);
  const [maxSalePrice, setMaxSalePrice] = useState<number | null>(null);
  const [minQuantity, setMinQuantity] = useState<number | null>(null);
  const [maxQuantity, setMaxQuantity] = useState<number | null>(null);
  const [createdFrom, setCreatedFrom] = useState<string | null>(null); // yyyy-MM-dd
  const [createdTo, setCreatedTo] = useState<string | null>(null);     // yyyy-MM-dd
  const [ecommerceFilter, setEcommerceFilter] = useState<'all' | 'yes' | 'no'>('all');
  const [onlyWithoutImage, setOnlyWithoutImage] = useState<boolean>(false);

  // Sayısal aralık filtreleri için debounce (her tuş vuruşunda istek atmamak için)
  const [appliedPriceQty, setAppliedPriceQty] = useState<{
    minSalePrice: number | null; maxSalePrice: number | null;
    minQuantity: number | null; maxQuantity: number | null;
  }>({ minSalePrice: null, maxSalePrice: null, minQuantity: null, maxQuantity: null });
  useEffect(() => {
    const h = setTimeout(() => {
      setAppliedPriceQty({ minSalePrice, maxSalePrice, minQuantity, maxQuantity });
    }, 500);
    return () => clearTimeout(h);
  }, [minSalePrice, maxSalePrice, minQuantity, maxQuantity]);

  // URL senkronizasyonu için: ilk hidrasyon tamamlandı mı?
  const urlHydratedRef = useRef<boolean>(false);
  const initialQueryRef = useRef<string>(typeof window !== 'undefined' ? window.location.search : '');
  
  // ✅ Merkezi Cache Sistemi - AuthContext'ten al
  const { 
    userDetails,
    categories, 
    brands, 
    warehouses, 
    branches,
    loadWarehouses,
    getCategoryName,
    getBrandName,
    getWarehouseName,
    getBranchName
  } = useAuth();

  // ✅ Kategori filtresi için hiyerarşik (ağaç sıralı) liste:
  // Ana kategori → altındaki alt kategoriler → onların altları (maks 3 seviye).
  // Autocomplete'te girintili gösterim bu sıralamaya dayanır.
  const hierarchicalCategories = useMemo(() => {
    const result: InventoryCategory[] = [];
    const addWithChildren = (parentId: string | null, depth: number) => {
      if (depth > 5) return; // döngüsel veri güvenliği
      categories
        .filter((c) => (c.parentId || null) === parentId)
        .sort((a, b) => a.name.localeCompare(b.name, 'tr'))
        .forEach((c) => {
          result.push(c);
          addWithChildren(c.id, depth + 1);
        });
    };
    addWithChildren(null, 1);
    // Güvenlik ağı: parent'ı silinmiş/bulunamayan kategoriler listeden düşmesin
    categories.forEach((c) => {
      if (!result.some((r) => r.id === c.id)) result.push(c);
    });
    return result;
  }, [categories]);

  // Bir kategorinin alt kategorisi var mı? (filtre seçeneğinde ipucu göstermek için)
  const categoryHasChildren = useCallback(
    (categoryId: string) => categories.some((c) => c.parentId === categoryId),
    [categories]
  );
  
  
  // ✅ YENİ: Server-side pagination hook
  const { 
    inventoryItems, 
    setInventoryItems, 
    loading: inventoryLoading, 
    error: inventoryError,
    totalCount, // ✅ Toplam ürün sayısı (server'dan)
    loadInventoryItemsPaged // ✅ Sayfa sayfa yükleme fonksiyonu
  } = useInventoryData(companyId, selectedWarehouse?.id || null);
  
  // Yenileme durumu için state
  const [refreshing, setRefreshing] = useState<boolean>(false);

  // Stok özet istatistikleri
  const [inventorySummary, setInventorySummary] = useState<{
    totalItems: number; activeItems: number; totalStockValue: number;
    totalStockValueSale: number;
    criticalStockCount: number; outOfStockCount: number;
    topSellingProductName: string | null; topSellingProductQty: number;
    idleProductCount: number;
  } | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);

  const loadInventorySummary = async (
    compId: string,
    filters?: {
      searchTerm?: string; categoryId?: string; brandId?: string;
      warehouseId?: string; isActive?: boolean; hideZeroPrice?: boolean;
      vatRate?: number; departmentNo?: number;
      isEcommerce?: boolean; branchId?: string; locationId?: string;
      minSalePrice?: number; maxSalePrice?: number;
      minQuantity?: number; maxQuantity?: number;
      createdFrom?: string; createdTo?: string;
      hideZeroStock?: boolean;
      onlyWithoutImage?: boolean;
    }
  ) => {
    try {
      setSummaryLoading(true);
      const response = await accountingApi.get(ACCOUNTING_API.INVENTORY_SUMMARY(filters));
      {
        const result = response.data;
        const data = result.data || result.Data || result;
        setInventorySummary({
          totalItems: data.totalItems ?? data.TotalItems ?? 0,
          activeItems: data.activeItems ?? data.ActiveItems ?? 0,
          totalStockValue: data.totalStockValue ?? data.TotalStockValue ?? 0,
          totalStockValueSale: data.totalStockValueSale ?? data.TotalStockValueSale ?? 0,
          criticalStockCount: data.criticalStockCount ?? data.CriticalStockCount ?? 0,
          outOfStockCount: data.outOfStockCount ?? data.OutOfStockCount ?? 0,
          topSellingProductName: data.topSellingProductName ?? data.TopSellingProductName ?? null,
          topSellingProductQty: data.topSellingProductQty ?? data.TopSellingProductQty ?? 0,
          idleProductCount: data.idleProductCount ?? data.IdleProductCount ?? 0,
        });
      }
    } catch (err) {
      console.error('❌ [INVENTORY] Summary yükleme hatası:', err);
    } finally {
      setSummaryLoading(false);
    }
  };
  
  // Toplu İşlemler Hook
  const bulkOps = useBulkOperations(companyId, inventoryItems);
  
  const [error, setError] = useState<string | null>(null);
  
  // Loading state'i hook'tan ve diğer yüklemelerden birleştir
  const loading = inventoryLoading;
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [filteredItems, setFilteredItems] = useState<InventoryItem[]>([]);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false); 
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [isProductFeatureModalOpen, setIsProductFeatureModalOpen] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isWarehouseModalOpen, setIsWarehouseModalOpen] = useState(false);
  const [isTableSettingsModalOpen, setIsTableSettingsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  
  // Sıralama için state'ler
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<string | null>(null);

  // Miktar ve fiyat güncelleme modalı için state
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [selectedField, setSelectedField] = useState<'quantity' | 'salePrice' | 'vatRate' | 'departmentNo' | 'name' | null>(null);
  const [updateStringValue, setUpdateStringValue] = useState<string>('');
  const [updateValue, setUpdateValue] = useState<number>(0);
  const [updateListPrice, setUpdateListPrice] = useState<number>(0);
  
  // Ayrıntılar modalı için state
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false); // ✅ YENİ
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false); // ✅ YENİ
  const [isQuickEditModalOpen, setIsQuickEditModalOpen] = useState(false); // ✅ YENİ
  
  // Sayfalama için state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);

  // Mobil/Desktop değişiminde rowsPerPage ayarla
  useEffect(() => {
    setRowsPerPage(isMobile ? 25 : 50);
  }, [isMobile]);

  // ✅ Search debounce: 400ms gecikme ile API'ye istek at
  useEffect(() => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
    searchDebounceRef.current = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 400);
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, [searchTerm]);

  // Tablo sütunları
  const [tableColumns, setTableColumns] = useState<TableColumn[]>([
    { id: 'rowNumber', label: 'Sıra', field: 'rowNumber', visible: true, fixed: true, description: 'Sıra numarası (sabit sütun)' },
    { id: 'name', label: 'Ürün Adı', field: 'name', visible: true, fixed: true, description: 'Ürün adı (sabit sütun)' },
    { id: 'barcode', label: 'Barkod', field: 'barcode', visible: true, fixed: false, description: 'Ürün barkodu' },
    { id: 'stockCode', label: 'Stok Kodu', field: 'stockCode', visible: true, fixed: false, description: 'Ürüne özel stok kodu' },
    { id: 'category', label: 'Kategori', field: 'categoryId', visible: true, fixed: false, description: 'Ürün kategorisi' },
    { id: 'brand', label: 'Marka', field: 'brandId', visible: true, fixed: false, description: 'Ürün markası' },
    { id: 'branch', label: 'Şube', field: 'branchesId', visible: false, fixed: false, description: 'Ürünün bulunduğu şube' },
    // ⚠️ TODO: quantity ve warehouse hierarchical view'de kullanılacak (alt satırlarda depo bazlı gösterim)
    { id: 'quantity', label: 'Miktar', field: 'quantity', visible: true, fixed: false, sortable: true, description: 'Toplam stok miktarı (tüm depolar)' },
    { id: 'unit', label: 'Birim', field: 'unit', visible: true, fixed: false, description: 'Ölçü birimi (adet, kg, vb.)' },
    { id: 'salePrice', label: 'Satış Fiyatı', field: 'salePrice', visible: true, fixed: true, sortable: true, description: 'Satış fiyatı (sabit sütun)' },
    { id: 'purchasePrice', label: 'Alış Fiyatı', field: 'purchasePrice', visible: false, fixed: false, description: 'Alış fiyatı' },
    { id: 'vatRate', label: 'KDV', field: 'vatRate', visible: false, fixed: false, description: 'KDV oranı' },
    { id: 'vatStatus', label: 'KDV Durumu', field: 'vatStatus', visible: false, fixed: false, description: 'KDV dahil/hariç' },
    { id: 'sctRate', label: 'ÖTV Oranı', field: 'sctRate', visible: false, fixed: false, description: 'ÖTV oranı' },
    { id: 'sctStatus', label: 'ÖTV Durumu', field: 'sctStatus', visible: false, fixed: false, description: 'ÖTV dahil/hariç' },
    { id: 'warehouse', label: 'Depo', field: 'warehouseId', visible: false, fixed: false, description: 'Depo bilgisi (hierarchical view\'de alt satırlarda)' },
    { id: 'location', label: 'Lokasyon', field: 'locationCode', visible: false, fixed: false, description: 'Akıllı depo lokasyon kodu' },
    { id: 'description', label: 'Açıklama', field: 'description', visible: false, fixed: false, description: 'Ürün açıklaması' },
    { id: 'ecommerce', label: 'E-Ticaret', field: 'ecommerce', visible: false, fixed: false, description: 'Ürünün e-ticaret durumu' },
    { id: 'qrmenu', label: 'QR Menü', field: 'qrmenu', visible: false, fixed: false, description: 'Ürünün QR Menü durumu' },
    { id: 'departmentNo', label: 'ÖKC', field: 'departmentNo', visible: false, fixed: false, description: 'Departman numarası (ÖKC)' },
    { id: 'canBeNegative', label: 'Eksi', field: 'canBeNegative', visible: false, fixed: false, description: 'Stok eksiye düşebilir mi?' },
    { id: 'actions', label: 'İşlemler', field: 'actions', visible: true, fixed: true, description: 'İşlem butonları (sabit sütun)' },
  ]);

  // Toplu işlem modu için yeni state değişkenleri
  const [bulkActionMode, setBulkActionMode] = useState<boolean>(false);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  
  // ✅ YENİ: Hierarchical view için expanded rows state
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  
  // Stoğu olmayan ürünleri gizle
  const [hideEmptyStock, setHideEmptyStock] = useState<boolean>(() => {
    // LocalStorage'dan değeri oku
    const saved = localStorage.getItem(HIDE_EMPTY_STOCK_KEY);
    return saved ? JSON.parse(saved) : false;
  });

  // Pasif ürünleri gizle
  const [hideInactiveItems, setHideInactiveItems] = useState<boolean>(() => {
    // ✅ Pasif ürünlerin görünmesi için LocalStorage'ı sıfırla
    // Kullanıcı isterse switch ile tekrar gizleyebilir
    localStorage.setItem(HIDE_INACTIVE_ITEMS_KEY, JSON.stringify(false));
    return false; // ✅ Her zaman false - pasif ürünler gösterilsin
  });

  // Stokları yeniden eskiye sırala (Varsayılan: false - alfabetik)
  const [sortNewestFirst, setSortNewestFirst] = useState<boolean>(() => {
    // LocalStorage'dan değeri oku
    const saved = localStorage.getItem('INVENTORY_SORT_NEWEST_FIRST');
    return saved ? JSON.parse(saved) : false; // Varsayılan: false - alfabetik sıralama
  });

  // Satış fiyatı 0 olanları gizle (Varsayılan: false - göster)
  const [hideZeroPrice, setHideZeroPrice] = useState<boolean>(() => {
    // LocalStorage'dan değeri oku
    const saved = localStorage.getItem('INVENTORY_HIDE_ZERO_PRICE');
    return saved ? JSON.parse(saved) : false; // Varsayılan: false - göster
  });

  // Alt varyantları (child variant) ana stok listesinde göster (Varsayılan: false - sadece ana ürünler)
  // Açıkken: backend excludeVariantChildren=false döner, child satırlar da listelenir.
  // Arama yapıldığında bu toggle'a bakılmadan child'lar otomatik dahil edilir.
  const [showVariantChildren, setShowVariantChildren] = useState<boolean>(() => {
    const saved = localStorage.getItem('INVENTORY_SHOW_VARIANT_CHILDREN');
    return saved ? JSON.parse(saved) : false;
  });

  // Genişletilmiş ürünleri takip eden state
  const [expandedItems, setExpandedItems] = useState<{ [key: string]: boolean }>({});

  // Bir ürünü genişlet/daralt
  const toggleItemExpand = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedItems(prev => ({
      ...prev,
      [itemId]: !prev[itemId]
    }));
  };

  // Bir ürünün alt ürünleri var mı kontrol et
  const hasChildItems = (itemId: string): boolean => {
    return inventoryItems.some(item => item.parentItemId === itemId);
  };

  // ✅ Server-side sorting: Sıralama artık backend'de yapılıyor, client-side sort gerekmez
  const sortedFilteredItems = useMemo(() => {
    return filteredItems;
  }, [filteredItems]);

  // Hiyerarşik ürün listesi oluştur
  const hierarchicalItems = useMemo(() => {
    // Depo filtresi seçiliyse veya kullanici "Alt varyantlari da goster" toggle'ini actiysa
    // hiyerarsik gruplamayi iptal edip tum kayitlari (parent + child) duz listede goster.
    // Aksi halde child'lar yalnizca parent expand edildiginde gorunur, bu da
    // toggle'in beklenen davranisini bozar.
    // Ayrica arama yapildigi durumda child'lar otomatik geldigi icin yine flat goster.
    if (selectedWarehouse || showVariantChildren || debouncedSearchTerm) {
      return sortedFilteredItems;
    }
    
    // Depo filtresi seçili değilse normal hiyerarşik yapıyı göster.
    // KALICI KURAL: parent/child ayriminda ana kaynak parentItemId'dir.
    // isMainItem legacy uyum alani oldugu icin burada karar verici olarak kullanilmaz.
    const parentItems = sortedFilteredItems.filter(item => 
      !item.parentItemId
    );
    
    // Sonra alt ürünleri grupla
    const childItemsMap: { [key: string]: InventoryItem[] } = {};
    
    sortedFilteredItems.forEach(item => {
      if (item.parentItemId) {
        if (!childItemsMap[item.parentItemId]) {
          childItemsMap[item.parentItemId] = [];
        }
        childItemsMap[item.parentItemId].push(item);
      }
    });
    
    // Ana ürünler ve genişletilmiş alt ürünleri içeren düz liste oluştur
    const result: InventoryItem[] = [];
    
    parentItems.forEach(parentItem => {
      // Ana ürünü ekle
      result.push(parentItem);
      
      // Genişletilmişse alt ürünleri ekle
      if (expandedItems[parentItem.id] && childItemsMap[parentItem.id]) {
        childItemsMap[parentItem.id].forEach(childItem => {
          result.push(childItem);
        });
      }
    });
    
    return result;
  }, [sortedFilteredItems, expandedItems, selectedWarehouse, showVariantChildren, debouncedSearchTerm]);

  // ✅ SERVER-SIDE PAGINATION: API'den gelen ürünleri doğrudan kullan (slice yok!)
  const paginatedItems = useMemo(() => {
    // Server-side pagination ile zaten doğru sayfa geldiği için slice yapmaya gerek yok
    return hierarchicalItems;
  }, [hierarchicalItems]);

  // LocalStorage'dan tablo ayarlarını yükle
  useEffect(() => {
    const savedSettings = localStorage.getItem(INVENTORY_TABLE_SETTINGS_KEY);
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        
        // Yeni eklenmiş sütun varsa, varsayılan ayarlarını koruyarak ekle
        const updatedColumns = tableColumns.map(defaultColumn => {
          const savedColumn = parsedSettings.find((col: TableColumn) => col.id === defaultColumn.id);
          if (savedColumn) {
            return {
              ...defaultColumn,
              visible: defaultColumn.fixed ? true : savedColumn.visible,
              sortable: savedColumn.sortable ?? defaultColumn.sortable ?? false,
            };
          }
          return defaultColumn;
        });
        
        setTableColumns(updatedColumns);
      } catch (e) {
        console.error('Kayıtlı tablo ayarları yüklenirken hata:', e);
      }
    }
  }, []);

  // companyId'yi AuthContext'ten al
  useEffect(() => {
    if (userDetails?.companyId) {
      setCompanyId(userDetails.companyId);
      
      // E-ticaret ve N11 entegrasyonlarını yükle
      loadEcommerceStatuses(userDetails.companyId);
      loadN11Integration(userDetails.companyId);
    }
  }, [userDetails?.companyId]);

  // Tablo ayarlarını kaydet
  const saveTableSettings = (columns: TableColumn[]) => {
    setTableColumns(columns);
    localStorage.setItem(INVENTORY_TABLE_SETTINGS_KEY, JSON.stringify(columns));
  };

  const isSmartWarehouse = selectedWarehouse?.warehouseType === 'Smart';

  useEffect(() => {
    setTableColumns(prev => prev.map(col =>
      col.id === 'location' ? { ...col, visible: isSmartWarehouse } : col
    ));
    setSelectedLocation(null);
    if (isSmartWarehouse && selectedWarehouse?.id) {
      (async () => {
        try {
          const resp = await accountingApi.get(ACCOUNTING_API.SWL_BY_WAREHOUSE(selectedWarehouse.id));
          const locs = (resp.data?.data || []).map((l: any) => ({ id: l.id, code: l.code, name: l.name }));
          setLocationOptions(locs);
        } catch { setLocationOptions([]); }
      })();
    } else {
      setLocationOptions([]);
    }
  }, [isSmartWarehouse, selectedWarehouse?.id]);

  // Effective excludeVariantChildren:
  // - Toggle kapalıyken (default) child'lar gizlenir.
  // - Arama yapılsa bile toggle davranışı korunur (kullanıcı "gizle" seçtiyse child gelmez).
  const effectiveExcludeVariantChildren = !showVariantChildren;

  // ✅ Server-side pagination + sorting: Debounced search ile veri çek
  useEffect(() => {
    if (!companyId) return;

    // E-ticaret: all = dokunma, yes = true, no = false
    const ecommerceParam = ecommerceFilter === 'all' ? undefined : ecommerceFilter === 'yes';
    // Lokasyon yalnızca akıllı depo seçiliyken anlamlı
    const locationParam = isSmartWarehouse ? (selectedLocation?.id || undefined) : undefined;
    // Tarih aralığı: gün başı/sonu sınırları (inclusive)
    const createdFromIso = createdFrom ? `${createdFrom}T00:00:00.000Z` : undefined;
    const createdToIso = createdTo ? `${createdTo}T23:59:59.999Z` : undefined;

    const advanced = {
      hideZeroStock: hideEmptyStock ? true : undefined,
      branchId: selectedBranch?.id || undefined,
      locationId: locationParam,
      minSalePrice: appliedPriceQty.minSalePrice ?? undefined,
      maxSalePrice: appliedPriceQty.maxSalePrice ?? undefined,
      minQuantity: appliedPriceQty.minQuantity ?? undefined,
      maxQuantity: appliedPriceQty.maxQuantity ?? undefined,
      createdFrom: createdFromIso,
      createdTo: createdToIso,
      onlyWithoutImage: onlyWithoutImage ? true : undefined,
    };

    loadInventorySummary(companyId, {
      searchTerm: debouncedSearchTerm || undefined,
      categoryId: selectedCategory?.id || undefined,
      brandId: selectedBrand?.id || undefined,
      warehouseId: selectedWarehouse?.id || undefined,
      isActive: hideInactiveItems ? true : undefined,
      hideZeroPrice: hideZeroPrice ? true : undefined,
      vatRate: selectedVatRate ?? undefined,
      departmentNo: selectedDepartmentNo ?? undefined,
      isEcommerce: ecommerceParam,
      branchId: selectedBranch?.id || undefined,
      locationId: locationParam,
      minSalePrice: appliedPriceQty.minSalePrice ?? undefined,
      maxSalePrice: appliedPriceQty.maxSalePrice ?? undefined,
      minQuantity: appliedPriceQty.minQuantity ?? undefined,
      maxQuantity: appliedPriceQty.maxQuantity ?? undefined,
      createdFrom: createdFromIso,
      createdTo: createdToIso,
      hideZeroStock: hideEmptyStock ? true : undefined,
      onlyWithoutImage: onlyWithoutImage ? true : undefined,
    });
    loadInventoryItemsPaged(
      companyId,
      page + 1, // MUI sayfa 0'dan başlar, backend 1'den başlar
      rowsPerPage,
      debouncedSearchTerm || undefined,
      selectedCategory?.id || undefined,
      selectedBrand?.id || undefined,
      selectedWarehouse?.id || undefined,
      hideInactiveItems ? true : undefined,
      ecommerceParam, // isEcommerce
      orderBy ? undefined : sortNewestFirst, // orderBy varsa sortNewestFirst'i backend'e gönderme
      hideZeroPrice ? true : undefined,
      selectedVatRate ?? undefined,
      selectedDepartmentNo ?? undefined,
      orderBy || undefined, // ✅ Server-side sort column
      orderBy ? order : undefined, // ✅ Server-side sort direction
      effectiveExcludeVariantChildren, // ✅ Alt varyantları gizle / göster (arama varsa otomatik göster)
      advanced // ✅ Gelişmiş filtreler (şube, lokasyon, stok, fiyat/miktar/tarih aralığı)
    );
  }, [companyId, page, rowsPerPage, debouncedSearchTerm, selectedCategory, selectedBrand, selectedWarehouse, selectedBranch, selectedLocation, isSmartWarehouse, hideInactiveItems, hideEmptyStock, sortNewestFirst, hideZeroPrice, selectedVatRate, selectedDepartmentNo, ecommerceFilter, onlyWithoutImage, appliedPriceQty, createdFrom, createdTo, order, orderBy, loadInventoryItemsPaged, effectiveExcludeVariantChildren]);
  
  // ⚠️ Eski client-side filtreleme artık kullanılmıyor - Server-side pagination yapıyoruz
  // Arama, marka, depo, şube filtreleri artık backend'den geliyor
  
  // Sayfa sıfırlama - filtre değiştiğinde ilk sayfaya dön
  useEffect(() => {
    if (page !== 0) {
      setPage(0); // Filtre değiştiğinde ilk sayfaya dön
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, selectedCategory, selectedBrand, selectedWarehouse, selectedBranch, selectedLocation, selectedVatRate, selectedDepartmentNo, showVariantChildren, hideEmptyStock, ecommerceFilter, onlyWithoutImage, appliedPriceQty, createdFrom, createdTo]);

  // GECICI DEFANSIF FILTRE:
  // Asil cozum backend'de InventoryFilterDto.ExcludeVariantChildren parametresiyle yapildi
  // (SQL'de WHERE ParentItemId IS NULL). Ancak bu degisiklik canliya deploy edilmediği surece
  // canli API child varyantlari da donduruyor. "Alt varyantları da göster" toggle'ı veya
  // arama aktifken child'lar zaten gosterilmek istendigi icin bu defansif filtre uygulanmaz.
  useEffect(() => {
    if (effectiveExcludeVariantChildren) {
      setFilteredItems(
        inventoryItems.filter(item => !item.parentItemId && !item.mainItemId)
      );
    } else {
      setFilteredItems(inventoryItems);
    }
  }, [inventoryItems, effectiveExcludeVariantChildren]);
  
  // Not: Çift tanımlama hatası düzeltildi - 'paginatedItems' değişkeni ikinci kez tanımlanmayacak
  // Bu noktada paginatedItems zaten hierarchicalItems üzerinden yukarıda tanımlanmış durumda
  
  // Sayfa değişimi - ✅ YENİ: Server'dan yeni sayfa çeker
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    // useEffect otomatik olarak yeni sayfayı yükleyecek
  };

  // Sayfa başına satır sayısı değişimi - ✅ YENİ: Server'dan yeni sayfa boyutu ile çeker
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const newPageSize = parseInt(event.target.value, 10);
    setRowsPerPage(newPageSize);
    setPage(0); // İlk sayfaya dön
    // useEffect otomatik olarak yeni sayfa boyutu ile yükleyecek
  };

  // Modal işlemleri
  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
    // ✅ Modal kapatıldığında mevcut sayfayı yenile
    if (companyId) {
      loadInventoryItemsPaged(
        companyId,
        page + 1,
        rowsPerPage,
        debouncedSearchTerm || undefined,
        selectedCategory?.id || undefined,
        selectedBrand?.id || undefined,
        selectedWarehouse?.id || undefined,
        hideInactiveItems ? true : undefined,
        undefined,
        orderBy ? undefined : sortNewestFirst,
        hideZeroPrice ? true : undefined,
        selectedVatRate ?? undefined,
        selectedDepartmentNo ?? undefined,
        orderBy || undefined,
        orderBy ? order : undefined,
        effectiveExcludeVariantChildren
      );
    }
  };

  const handleOpenQuickAddModal = () => {
    setIsQuickAddModalOpen(true);
  };

  const handleCloseQuickAddModal = () => {
    setIsQuickAddModalOpen(false);
    // ✅ Modal kapatıldığında mevcut sayfayı yenile
    if (companyId) {
      loadInventoryItemsPaged(
        companyId,
        page + 1,
        rowsPerPage,
        debouncedSearchTerm || undefined,
        selectedCategory?.id || undefined,
        selectedBrand?.id || undefined,
        selectedWarehouse?.id || undefined,
        hideInactiveItems ? true : undefined,
        undefined,
        orderBy ? undefined : sortNewestFirst,
        hideZeroPrice ? true : undefined,
        selectedVatRate ?? undefined,
        selectedDepartmentNo ?? undefined,
        orderBy || undefined,
        orderBy ? order : undefined,
        effectiveExcludeVariantChildren
      );
    }
  };

  const handleOpenCategoryModal = () => {
    setIsCategoryModalOpen(true);
  };

  const handleCloseCategoryModal = () => {
    setIsCategoryModalOpen(false);
  };

  const handleOpenProductFeatureModal = () => {
    setIsProductFeatureModalOpen(true);
  };

  const handleCloseProductFeatureModal = () => {
    setIsProductFeatureModalOpen(false);
  };

  const handleOpenBrandModal = () => {
    setIsBrandModalOpen(true);
  };

  const handleCloseBrandModal = () => {
    setIsBrandModalOpen(false);
  };

  const handleOpenWarehouseModal = () => {
    setIsWarehouseModalOpen(true);
  };

  const handleCloseWarehouseModal = () => {
    setIsWarehouseModalOpen(false);
  };

  const handleOpenTableSettingsModal = () => {
    setIsTableSettingsModalOpen(true);
  };

  const handleCloseTableSettingsModal = () => {
    setIsTableSettingsModalOpen(false);
  };

  const handleOpenImportModal = () => {
    // İçe aktarma modalı açılırken realtime listener'ı durdur
    setRealtimeListenerActive(false);
    setIsImportModalOpen(true);
  };

  const handleCloseImportModal = () => {
    // İçe aktarma modalı kapanırken realtime listener'ı tekrar aktifleştir
    setRealtimeListenerActive(true);
    setIsImportModalOpen(false);
    // ✅ Modal kapatıldığında mevcut sayfayı yenile
    if (companyId) {
      loadInventoryItemsPaged(
        companyId,
        page + 1,
        rowsPerPage,
        debouncedSearchTerm || undefined,
        selectedCategory?.id || undefined,
        selectedBrand?.id || undefined,
        selectedWarehouse?.id || undefined,
        hideInactiveItems ? true : undefined,
        undefined,
        orderBy ? undefined : sortNewestFirst,
        hideZeroPrice ? true : undefined,
        selectedVatRate ?? undefined,
        selectedDepartmentNo ?? undefined,
        orderBy || undefined,
        orderBy ? order : undefined,
        effectiveExcludeVariantChildren
      );
    }
  };

  // Tablo sütun ayarlarını güncelle
  const handleUpdateTableSettings = (columns: TableColumn[]) => {
    saveTableSettings(columns);
  };

  // Aktif sütunların listesini oluştur
  const visibleColumns = tableColumns.filter(column => column.visible);

  const [selectedItem, setSelectedItem] = useState<InventoryItem | null>(null);
  const [selectedStockContext, setSelectedStockContext] = useState<{
    warehouseStockId?: string;
    warehouseId: string;
    locationId?: string;
  } | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleOpenDeleteDialog = (item: InventoryItem) => {
    setSelectedItem(item);
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setSelectedItem(null);
  };

  const handleDeleteItem = async () => {
    if (!selectedItem || !companyId) return;
    
    setIsDeleting(true);
    
    try {
      const itemIdToDelete = selectedItem.id;
      const itemName = selectedItem.name;
      
      // ✅ SQL kullanarak soft delete (POST)
      await InventoryService.deleteItem(companyId, itemIdToDelete, companyId);
      
      // ✅ State'den hemen kaldır (UI'dan anında gitsin)
      setInventoryItems(prevItems => 
        prevItems.filter(item => item.id !== itemIdToDelete)
      );
      
      setDeleteDialogOpen(false);
      setIsDeleteModalOpen(false); // ✅ YENİ: Modal kapatma
      setSelectedItem(null);
      setError(null);
      
      showSnackbar(`${itemName} başarıyla silindi`, 'success');
      
      // ✅ Database commit'in tamamlanması için kısa bir bekleme
      await new Promise(resolve => setTimeout(resolve, 500));
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 
                          error.message || 
                          'Stok öğesi silinirken bir hata oluştu';
      setError(errorMessage);
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  // ✅ YENİ: Ürün kopyalama fonksiyonu
  const handleDuplicateItem = async (item: InventoryItem) => {
    if (!companyId) return;
    
    try {
      const duplicatedItem: any = {
        ...item,
        companyId: companyId,
        name: `${item.name} (Kopya)`,
        barcode: `${item.barcode || ''}-KOPYA`,
        stockCode: `${item.stockCode || ''}-KOPYA`,
        saleCurrency: item.saleCurrency || 'TRY', // ✅ Default değer
      };
      
      await InventoryService.createItem(duplicatedItem);
      showSnackbar('Ürün başarıyla kopyalandı.', 'success');
    } catch (error: any) {
      showSnackbar(error.message || 'Ürün kopyalanırken hata oluştu!', 'error');
    }
  };

  // ✅ YENİ: E-ticaret durumunu değiştir
  const handleToggleEcommerce = async (item: InventoryItem) => {
    if (!companyId) return;
    
    try {
      await InventoryService.toggleEcommerce(
        companyId,
        item.id,
        !item.isEcommerce
      );
      
      showSnackbar(
        `E-ticaret durumu ${!item.isEcommerce ? 'açıldı' : 'kapatıldı'}.`,
        'success'
      );
    } catch (error: any) {
      showSnackbar(error.message || 'E-ticaret durumu değiştirilirken hata oluştu!', 'error');
    }
  };

  // Miktar, fiyat, KDV, ÖKC, Ürün Adı veya Detaylar hücresine tıklanınca modalı aç
  const handleCellClick = async (item: InventoryItem, field: 'quantity' | 'salePrice' | 'vatRate' | 'departmentNo' | 'name' | 'details') => {
    // Eğer 'details' field'ı ise stok ayrıntıları modalını aç
    if (field === 'details') {
      setSelectedItem(item);
      setIsDetailModalOpen(true);
      return;
    }
    
    // ✅ Pasif ürünlerde değerler güncellenemez
    if (!item.isActive) {
      showSnackbar('Pasif ürünlerde değerler güncellenemez. Lütfen önce ürünü aktifleştirin.', 'warning');
      return;
    }
    
    // Güncel veriyi inventoryItems'dan al
    const currentItem = inventoryItems.find(invItem => invItem.id === item.id) || item;
    
    // ✅ Mevcut state'teki veriyi kullan
    setSelectedItem(currentItem);
    setSelectedField(field);
    setSelectedStockContext(null); // Genel satırdan açılıyorsa depo/lokasyon bağlamı sıfırlanır
    
    // Field'a göre uygun değeri set et
    if (field === 'quantity') {
      const totalQuantity = currentItem.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0;
      setUpdateValue(totalQuantity);
    } else if (field === 'salePrice') {
      setUpdateValue(currentItem.salePrice);
      setUpdateListPrice((currentItem as any).listPrice || 0);
    } else if (field === 'vatRate') {
      setUpdateValue(currentItem.vatRate || 0);
    } else if (field === 'departmentNo') {
      setUpdateValue(currentItem.departmentNo || 0);
    } else if (field === 'name') {
      setUpdateStringValue(currentItem.name);
    }
    
    setIsUpdateModalOpen(true);
  };

  const handleWarehouseStockQuantityClick = (item: InventoryItem, stock: WarehouseStockDto) => {
    if (!item.isActive) {
      showSnackbar('Pasif ürünlerde değerler güncellenemez. Lütfen önce ürünü aktifleştirin.', 'warning');
      return;
    }

    const currentItem = inventoryItems.find(invItem => invItem.id === item.id) || item;
    const targetedStock = stock;

    setSelectedItem({
      ...currentItem,
      warehouseStocks: [targetedStock],
    });
    setSelectedField('quantity');
    setSelectedStockContext({
      warehouseStockId: targetedStock.id,
      warehouseId: targetedStock.warehouseId,
      locationId: targetedStock.locationId,
    });
    setUpdateValue(targetedStock.quantity || 0);
    setIsUpdateModalOpen(true);
  };

  // Modal kapatma
  const handleCloseUpdateModal = () => {
    setIsUpdateModalOpen(false);
    setSelectedItem(null);
    setSelectedField(null);
    setSelectedStockContext(null);
    setUpdateListPrice(0); // Liste fiyatını sıfırla
    setUpdateStringValue(''); // String değerini sıfırla
    setSyncWithN11(false); // N11 senkronizasyon checkbox'ını sıfırla
  };

  // Değer güncelleme
  const handleUpdateValue = async (movementData?: {
    movementDirection?: 'IN' | 'OUT';
    referenceNumber?: string;
    description?: string;
    date?: Date;
    unitPrice?: number;
    warehouseStockId?: string;
    warehouseId?: string;
    locationId?: string;
  }) => {
    const fallbackSingleStock =
      selectedItem?.warehouseStocks && selectedItem.warehouseStocks.length === 1
        ? selectedItem.warehouseStocks[0]
        : undefined;

    await valueUpdateHook.handleUpdateValue({
            selectedItem,
            selectedField,
            updateValue,
      updateListPrice,
      updateStringValue,
      syncWithN11,
      userDetails,
      movementData: {
        ...movementData,
        warehouseStockId:
          movementData?.warehouseStockId ||
          selectedStockContext?.warehouseStockId ||
          fallbackSingleStock?.id,
        warehouseId:
          movementData?.warehouseId ||
          selectedStockContext?.warehouseId ||
          fallbackSingleStock?.warehouseId,
        locationId:
          movementData?.locationId ??
          selectedStockContext?.locationId ??
          fallbackSingleStock?.locationId,
      },
    });
  };

  // ✅ Excel export artık hook'tan geliyor - loadingExport ve exportToExcel hook'ta

  // getCellValue wrapper - utility fonksiyonuna options geçir
  const getCellValueWrapper = (item: InventoryItem, columnId: string, rowIndex?: number): React.ReactNode => {
    return getCellValue(item, columnId, {
      getCategoryName,
      getBrandName,
      getBranchName,
      getWarehouseName,
      localizeUnit,
      ecommerceStatuses,
      onCellClick: handleCellClick,
      onMenuOpen: handleMenuOpen,
      onOpenImageLibraryModal: handleOpenImageLibraryModal,
      rowIndex,
      page,
      rowsPerPage,
    });
  };

  // Edit butonuna tıklandığında - AddInventoryItem modalını düzenleme modunda aç
  const handleEditItem = (item: InventoryItem) => {
    setSelectedItem(item);
    setIsEditModalOpen(true); // AddInventoryItem modalını açacak
  };

  // Detay modalını kapatma
  const handleCloseDetailModal = () => {
    setIsDetailModalOpen(false);
    setSelectedItem(null);
  };

  // Stoğu olmayan ürünleri gizle durumunu değiştir
  const handleHideEmptyStockChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setHideEmptyStock(newValue);
    localStorage.setItem(HIDE_EMPTY_STOCK_KEY, JSON.stringify(newValue));
  };

  // Pasif ürünleri gizle durumunu değiştir
  const handleHideInactiveItemsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    setHideInactiveItems(newValue);
    localStorage.setItem(HIDE_INACTIVE_ITEMS_KEY, JSON.stringify(newValue));
  };

  // Ürünü aktif et
  // Pasif ürünü aktifleştir
  const handleActivateItem = async () => {
    if (!menuItemId) return;
    await activateItem(menuItemId);
      handleMenuClose();
  };

  // Toplu işlem modunu aç/kapat
  const toggleBulkActionMode = () => {
    // Mobilde modal açalım
    if (isMobile && !bulkActionMode) {
      setIsBulkActionSelectionModalOpen(true);
      return;
    }
    
    // Desktop veya kapatma işlemi
    setBulkActionMode(!bulkActionMode);
    
    // Eğer toplu işlem modu kapatılırsa, seçili öğeleri ve aksiyonu temizle
    if (bulkActionMode) {
      setSelectedItems([]);
      setSelectedBulkAction(null);
    }
  };

  // Mobil için toplu işlem seçimi yap
  const handleSelectBulkAction = (action: string) => {
    setSelectedBulkAction(action);
    setBulkActionMode(true);
    setIsBulkActionSelectionModalOpen(false);
  };

  // Öğeyi seçme/seçimi kaldırma işlemi
  const handleSelectItem = (id: string) => {
    setSelectedItems(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Tüm görünür öğeleri seç/seçimini kaldır
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const allPageItemIds = paginatedItems.map(item => item.id);
      setSelectedItems(allPageItemIds);
    } else {
      setSelectedItems([]);
    }
  };
  
  // ✅ YENİ: Row expand/collapse toggle
  const handleToggleRow = (itemId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  // Ana ürün güncellendiğinde tüm kopyalarına değişiklikleri yansıt (SQL API)
  const handleUpdateMainItem = async (mainItem: InventoryItem, childItems: InventoryItem[]) => {
    if (!companyId) return;
    
    setError(null);
    
    try {
      // Tüm alt ürünleri SQL API ile güncelle
      const updatePromises = childItems.map(child => {
        const updatedChild = {
          ...child,
          name: mainItem.name,
          barcode: mainItem.barcode,
          categoryId: mainItem.categoryId,
          brandId: mainItem.brandId,
          stockCode: mainItem.stockCode,
          unit: mainItem.unit,
          vatRate: mainItem.vatRate,
          vatStatus: mainItem.vatStatus,
          sctRate: mainItem.sctRate,
          sctStatus: mainItem.sctStatus,
          description: mainItem.description,
          isActive: mainItem.isActive,
        };
        return InventoryService.updateItem(companyId, child.id, updatedChild);
      });
      
      if (updatePromises.length > 0) {
        await Promise.all(updatePromises);
        console.log(`${childItems.length} alt ürün güncellendi (SQL API)`);
      }
    } catch (error) {
      console.error('Alt ürünleri güncellerken bir hata oluştu:', error);
      setError('Alt ürünleri güncellerken bir hata oluştu.');
    }
  };

  // Stok Hareket Raporu sayfasına gitme
  const handleStockMovementsReport = () => {
    navigate('/stok-hareket-raporu');
  };

  // InventoryImportModal için fonksiyon referansını kaydet
  useEffect(() => {
    // Fonksiyon referansını dışarıdan erişilebilir hale getir
    setRealtimeListenerToggle(setRealtimeListenerActive);
  }, []);

  // Tüm stokları silme işlemi için state değişkenleri
  const [showDeleteAllDialog, setShowDeleteAllDialog] = useState<boolean>(false);
  const [deletingAll, setDeletingAll] = useState<boolean>(false);
  const [deleteProgress, setDeleteProgress] = useState<number>(0);
  const [totalItemsToDelete, setTotalItemsToDelete] = useState<number>(0);
  const [deletedItemsCount, setDeletedItemsCount] = useState<number>(0);

  // Tüm stokları silme işlemini başlat
  const handleDeleteAllItems = () => {
    const itemsToDelete = selectedWarehouse 
      ? filteredItems.filter(item => 
          item.warehouseStocks?.some(ws => ws.warehouseId === selectedWarehouse.id)
        )
      : filteredItems;
    
    if (itemsToDelete.length === 0) {
      setError(selectedWarehouse 
        ? 'Seçili depoda silinecek ürün bulunmuyor' 
        : 'Silinecek ürün bulunmuyor');
      return;
    }
    
    setTotalItemsToDelete(itemsToDelete.length);
    setDeletedItemsCount(0);
    setDeleteProgress(0);
    setShowDeleteAllDialog(true);
  };

  // Tüm stokları silme işlemini onayla ve gerçekleştir
  const confirmDeleteAllItems = async () => {
    if (!companyId) {
      setShowDeleteAllDialog(false);
      return;
    }

    const itemsToDelete = selectedWarehouse 
      ? filteredItems.filter(item => 
          item.warehouseStocks?.some(ws => ws.warehouseId === selectedWarehouse.id)
        )
      : filteredItems;
    
    if (itemsToDelete.length === 0) {
      setShowDeleteAllDialog(false);
      return;
    }

    setDeletingAll(true);
    setDeletedItemsCount(0);
    setDeleteProgress(0);

    try {
      // ✅ InventoryService.deleteAllItems kullan (SQL API)
      await InventoryService.deleteAllItems(
        companyId,
        selectedWarehouse?.id,
        (deleted: number, total: number) => {
          setDeletedItemsCount(deleted);
          const percentage = Math.round((deleted / total) * 100);
          setDeleteProgress(percentage);
        }
      );
      
      // State'den silinen ürünleri kaldır
      setInventoryItems(prevItems => 
        prevItems.filter(item => !itemsToDelete.some(deletedItem => deletedItem.id === item.id))
      );
      
      // İşlem tamamlandı
      setError(null);
      showSnackbar(`${itemsToDelete.length} ürün başarıyla silindi`, 'success');
      
    } catch (error) {
      console.error("Tüm stokları silme sırasında hata oluştu:", error);
      setError('Tüm stokları silme işlemi sırasında bir hata oluştu');
      showSnackbar('Tüm stokları silme işlemi sırasında bir hata oluştu', 'error');
    } finally {
      setTimeout(() => {
        setDeletingAll(false);
        setShowDeleteAllDialog(false);
      }, 1000); // İşlem bittiğini göstermek için 1 saniye bekle
    }
  };

  // Tüm stokları silme işlemini iptal et
  const cancelDeleteAllItems = () => {
    if (deletingAll) return; // Silme işlemi devam ediyorsa iptal etme
    setShowDeleteAllDialog(false);
  };

  // Menü için state değişkenleri
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [menuItemId, setMenuItemId] = useState<string | null>(null);
  
  // E-ticaret durumları için state
  const [ecommerceStatuses, setEcommerceStatuses] = useState<{[key: string]: boolean}>({});
  
  // Toplu e-ticaret işlemleri için state
  const [bulkEcommerceProcessing, setBulkEcommerceProcessing] = useState<boolean>(false);
  const [showBulkEcommerceDialog, setShowBulkEcommerceDialog] = useState<boolean>(false);
  const [bulkEcommerceAction, setBulkEcommerceAction] = useState<'open' | 'close' | null>(null);

  // N11 entegrasyon durumu için state
  const [n11IntegrationActive, setN11IntegrationActive] = useState<boolean>(false);
  const [isN11UploadModalOpen, setIsN11UploadModalOpen] = useState<boolean>(false);
  
  // N11 kategori seçim modal'ı için state
  const [isN11CategoryModalOpen, setIsN11CategoryModalOpen] = useState<boolean>(false);
  const [selectedItemForN11, setSelectedItemForN11] = useState<InventoryItem | null>(null);
  // ✅ n11SendingProduct artık hook'ta

  // Snackbar için state
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');

  // N11 senkronizasyon için state
  const [syncWithN11, setSyncWithN11] = useState<boolean>(false);
  // ✅ N11 syncing artık valueUpdateHook'ta

  // Resim kütüphanesi seçme modalı için state
  const [isImageLibraryModalOpen, setIsImageLibraryModalOpen] = useState<boolean>(false);
  const [selectedImageItem, setSelectedImageItem] = useState<InventoryItem | null>(null);

  // ✅ YENİ: Resim menüsü için state'ler
  const [imageMenuAnchor, setImageMenuAnchor] = useState<HTMLElement | null>(null);
  const [isImageViewModalOpen, setIsImageViewModalOpen] = useState<boolean>(false);
  const [isRemoveImageConfirmOpen, setIsRemoveImageConfirmOpen] = useState<boolean>(false);
  const [isRemovingImage, setIsRemovingImage] = useState<boolean>(false);

  // N11 Log modalı için state
  const [isN11LogModalOpen, setIsN11LogModalOpen] = useState<boolean>(false);

  // Kargo şablonları modalı için state
  const [isShipmentTemplatesModalOpen, setIsShipmentTemplatesModalOpen] = useState<boolean>(false);

  // Diğer işlemler modalı için state (mobil)
  const [isOtherActionsModalOpen, setIsOtherActionsModalOpen] = useState<boolean>(false);

  // ✅ YENİ: Şube Fiyatı Modalı
  const [isBranchPriceModalOpen, setIsBranchPriceModalOpen] = useState<boolean>(false);
  const [branchPriceSelectedItem, setBranchPriceSelectedItem] = useState<InventoryItem | null>(null);

  // ✅ YENİ: Basit Üretim Reçetesi Modalı
  const [isRecipeModalOpen, setIsRecipeModalOpen] = useState<boolean>(false);
  const [recipeSelectedItem, setRecipeSelectedItem] = useState<InventoryItem | null>(null);

  // ✅ YENİ: Menü Tanımları Modalı
  const [isProductMenuModalOpen, setIsProductMenuModalOpen] = useState<boolean>(false);
  const [productMenuSelectedItem, setProductMenuSelectedItem] = useState<InventoryItem | null>(null);

  // ✅ YENİ: Varyant Yonetim Modalı (frontend iskelet)
  const [isVariantManagementModalOpen, setIsVariantManagementModalOpen] = useState<boolean>(false);
  const [variantManagementSelectedItem, setVariantManagementSelectedItem] = useState<InventoryItem | null>(null);

  // Filtreleri göster/gizle (mobil ve desktop)
  const [showFilters, setShowFilters] = useState<boolean>(false); // Varsayılan: gizli

  // Tüm filtreleri temizle (chip'lerdeki "Tümünü temizle" + AI clearAll için ortak)
  const resetAllFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setSelectedCategory(null);
    setSelectedBrand(null);
    setSelectedWarehouse(null);
    setSelectedBranch(null);
    setSelectedLocation(null);
    setSelectedVatRate(null);
    setSelectedDepartmentNo(null);
    setHideInactiveItems(false);
    setHideZeroPrice(false);
    setHideEmptyStock(false);
    localStorage.setItem(HIDE_EMPTY_STOCK_KEY, JSON.stringify(false));
    setEcommerceFilter('all');
    setOnlyWithoutImage(false);
    setMinSalePrice(null);
    setMaxSalePrice(null);
    setMinQuantity(null);
    setMaxQuantity(null);
    setCreatedFrom(null);
    setCreatedTo(null);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 🔗 URL → filtre state hidrasyonu (derin link / geri-ileri).
  // Yalnızca ilk kez; lookup'lar (kategori/marka/depo/şube) hazır olana kadar bekler.
  useEffect(() => {
    if (urlHydratedRef.current) return;
    const params = new URLSearchParams(initialQueryRef.current);
    const hasIdFilter = params.has('cat') || params.has('brand') || params.has('wh') || params.has('branch');
    const lookupsEmpty = categories.length === 0 && brands.length === 0 && warehouses.length === 0 && branches.length === 0;
    if (hasIdFilter && lookupsEmpty) return; // lookup'lar gelene kadar hidrasyonu ertele

    const q = params.get('q'); if (q) { setSearchTerm(q); setDebouncedSearchTerm(q); }
    const vat = params.get('vat'); if (vat !== null) setSelectedVatRate(Number(vat));
    const dept = params.get('dept'); if (dept !== null) setSelectedDepartmentNo(Number(dept));
    if (params.get('inactive') === '1') setHideInactiveItems(true);
    if (params.get('zeroPrice') === '1') setHideZeroPrice(true);
    if (params.get('zeroStock') === '1') { setHideEmptyStock(true); localStorage.setItem(HIDE_EMPTY_STOCK_KEY, JSON.stringify(true)); }
    const ecom = params.get('ecom'); if (ecom === 'yes' || ecom === 'no') setEcommerceFilter(ecom);
    if (params.get('noImg') === '1') setOnlyWithoutImage(true);
    const minP = params.get('minP'); if (minP !== null) setMinSalePrice(Number(minP));
    const maxP = params.get('maxP'); if (maxP !== null) setMaxSalePrice(Number(maxP));
    const minQ = params.get('minQ'); if (minQ !== null) setMinQuantity(Number(minQ));
    const maxQ = params.get('maxQ'); if (maxQ !== null) setMaxQuantity(Number(maxQ));
    const cFrom = params.get('cFrom'); if (cFrom) setCreatedFrom(cFrom);
    const cTo = params.get('cTo'); if (cTo) setCreatedTo(cTo);

    const cat = params.get('cat'); if (cat) setSelectedCategory(categories.find((c) => c.id === cat) ?? null);
    const brand = params.get('brand'); if (brand) setSelectedBrand(brands.find((b) => b.id === brand) ?? null);
    const wh = params.get('wh'); if (wh) setSelectedWarehouse(warehouses.find((w) => w.id === wh) ?? null);
    const branch = params.get('branch'); if (branch) setSelectedBranch(branches.find((b) => String(b.id) === branch) ?? null);

    urlHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, brands, warehouses, branches]);

  // 🔗 Filtre state → URL (paylaşılabilir / geri-ileri). Hidrasyon bitmeden yazma.
  useEffect(() => {
    if (!urlHydratedRef.current) return;
    const params = new URLSearchParams();
    if (debouncedSearchTerm) params.set('q', debouncedSearchTerm);
    if (selectedCategory) params.set('cat', selectedCategory.id);
    if (selectedBrand) params.set('brand', selectedBrand.id);
    if (selectedWarehouse) params.set('wh', selectedWarehouse.id);
    if (selectedBranch) params.set('branch', String(selectedBranch.id));
    if (selectedVatRate !== null) params.set('vat', String(selectedVatRate));
    if (selectedDepartmentNo !== null) params.set('dept', String(selectedDepartmentNo));
    if (hideInactiveItems) params.set('inactive', '1');
    if (hideZeroPrice) params.set('zeroPrice', '1');
    if (hideEmptyStock) params.set('zeroStock', '1');
    if (ecommerceFilter !== 'all') params.set('ecom', ecommerceFilter);
    if (onlyWithoutImage) params.set('noImg', '1');
    if (minSalePrice !== null) params.set('minP', String(minSalePrice));
    if (maxSalePrice !== null) params.set('maxP', String(maxSalePrice));
    if (minQuantity !== null) params.set('minQ', String(minQuantity));
    if (maxQuantity !== null) params.set('maxQ', String(maxQuantity));
    if (createdFrom) params.set('cFrom', createdFrom);
    if (createdTo) params.set('cTo', createdTo);
    setSearchParams(params, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm, selectedCategory, selectedBrand, selectedWarehouse, selectedBranch, selectedVatRate, selectedDepartmentNo, hideInactiveItems, hideZeroPrice, hideEmptyStock, ecommerceFilter, onlyWithoutImage, minSalePrice, maxSalePrice, minQuantity, maxQuantity, createdFrom, createdTo]);

  // Popup'a verilen "şu an uygulanan" filtre taslağı
  const appliedFilterDraft: InventoryFilterDraft = useMemo(() => ({
    category: selectedCategory,
    brand: selectedBrand,
    warehouse: selectedWarehouse,
    branch: selectedBranch,
    location: selectedLocation,
    vatRate: selectedVatRate,
    departmentNo: selectedDepartmentNo,
    minSalePrice,
    maxSalePrice,
    minQuantity,
    maxQuantity,
    createdFrom,
    createdTo,
    ecommerceFilter,
    onlyWithoutImage,
  }), [
    selectedCategory, selectedBrand, selectedWarehouse, selectedBranch, selectedLocation,
    selectedVatRate, selectedDepartmentNo, minSalePrice, maxSalePrice, minQuantity, maxQuantity,
    createdFrom, createdTo, ecommerceFilter, onlyWithoutImage,
  ]);

  // Popup "Uygula" — tüm filtreleri tek seferde yaz + debounce'u flush et + sayfa 0
  const handleApplyFilters = useCallback((draft: InventoryFilterDraft) => {
    setSelectedCategory(draft.category);
    setSelectedBrand(draft.brand);
    setSelectedWarehouse(draft.warehouse);
    setSelectedBranch(draft.branch);
    setSelectedLocation(draft.location);
    setSelectedVatRate(draft.vatRate);
    setSelectedDepartmentNo(draft.departmentNo);
    setMinSalePrice(draft.minSalePrice);
    setMaxSalePrice(draft.maxSalePrice);
    setMinQuantity(draft.minQuantity);
    setMaxQuantity(draft.maxQuantity);
    // Fiyat/miktar debounce'unu atla — hemen uygula
    setAppliedPriceQty({
      minSalePrice: draft.minSalePrice,
      maxSalePrice: draft.maxSalePrice,
      minQuantity: draft.minQuantity,
      maxQuantity: draft.maxQuantity,
    });
    setCreatedFrom(draft.createdFrom);
    setCreatedTo(draft.createdTo);
    setEcommerceFilter(draft.ecommerceFilter);
    setOnlyWithoutImage(draft.onlyWithoutImage);
    setPage(0);
    setShowFilters(false);
  }, []);

  // Aktif filtre chip'leri (panelde gösterilir; tek tek kaldırılabilir)
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; onDelete: () => void }[] = [];
    if (selectedCategory) chips.push({ key: 'cat', label: `Kategori: ${selectedCategory.name}`, onDelete: () => setSelectedCategory(null) });
    if (selectedBrand) chips.push({ key: 'brand', label: `Marka: ${selectedBrand.name}`, onDelete: () => setSelectedBrand(null) });
    if (selectedWarehouse) chips.push({ key: 'wh', label: `Depo: ${selectedWarehouse.name}`, onDelete: () => setSelectedWarehouse(null) });
    if (selectedBranch) chips.push({ key: 'branch', label: `Şube: ${selectedBranch.name}`, onDelete: () => setSelectedBranch(null) });
    if (selectedLocation) chips.push({ key: 'loc', label: `Lokasyon: ${selectedLocation.code}`, onDelete: () => setSelectedLocation(null) });
    if (selectedVatRate !== null) chips.push({ key: 'vat', label: `KDV: %${selectedVatRate}`, onDelete: () => setSelectedVatRate(null) });
    if (selectedDepartmentNo !== null) chips.push({ key: 'dept', label: `ÖKC: ${selectedDepartmentNo}`, onDelete: () => setSelectedDepartmentNo(null) });
    if (hideInactiveItems) chips.push({ key: 'inactive', label: 'Pasif gizli', onDelete: () => setHideInactiveItems(false) });
    if (hideZeroPrice) chips.push({ key: 'zeroPrice', label: 'Fiyatı 0 gizli', onDelete: () => setHideZeroPrice(false) });
    if (hideEmptyStock) chips.push({ key: 'zeroStock', label: 'Stoğu olmayan gizli', onDelete: () => { setHideEmptyStock(false); localStorage.setItem(HIDE_EMPTY_STOCK_KEY, JSON.stringify(false)); } });
    if (ecommerceFilter !== 'all') chips.push({ key: 'ecom', label: `E-Ticaret: ${ecommerceFilter === 'yes' ? 'Açık' : 'Kapalı'}`, onDelete: () => setEcommerceFilter('all') });
    if (onlyWithoutImage) chips.push({ key: 'noImg', label: 'Resmi olmayanlar', onDelete: () => setOnlyWithoutImage(false) });
    if (minSalePrice !== null) chips.push({
      key: 'minP',
      label: `Min fiyat: ${minSalePrice}`,
      onDelete: () => {
        setMinSalePrice(null);
        setAppliedPriceQty((prev) => ({ ...prev, minSalePrice: null }));
      },
    });
    if (maxSalePrice !== null) chips.push({
      key: 'maxP',
      label: `Max fiyat: ${maxSalePrice}`,
      onDelete: () => {
        setMaxSalePrice(null);
        setAppliedPriceQty((prev) => ({ ...prev, maxSalePrice: null }));
      },
    });
    if (minQuantity !== null) chips.push({
      key: 'minQ',
      label: `Min stok: ${minQuantity}`,
      onDelete: () => {
        setMinQuantity(null);
        setAppliedPriceQty((prev) => ({ ...prev, minQuantity: null }));
      },
    });
    if (maxQuantity !== null) chips.push({
      key: 'maxQ',
      label: `Max stok: ${maxQuantity}`,
      onDelete: () => {
        setMaxQuantity(null);
        setAppliedPriceQty((prev) => ({ ...prev, maxQuantity: null }));
      },
    });
    if (createdFrom) chips.push({ key: 'cFrom', label: `Başlangıç: ${createdFrom}`, onDelete: () => setCreatedFrom(null) });
    if (createdTo) chips.push({ key: 'cTo', label: `Bitiş: ${createdTo}`, onDelete: () => setCreatedTo(null) });
    return chips;
  }, [selectedCategory, selectedBrand, selectedWarehouse, selectedBranch, selectedLocation, selectedVatRate, selectedDepartmentNo, hideInactiveItems, hideZeroPrice, hideEmptyStock, ecommerceFilter, onlyWithoutImage, minSalePrice, maxSalePrice, minQuantity, maxQuantity, createdFrom, createdTo]);

  // 🤖 AI ajanı köprüsü: asistan "sayfada bul/filtrele" eylemini çalıştırınca
  // (applyInventoryFilters) chat executor'ı bir istek yayınlar; burada sayfanın
  // kendi filtre state'lerine uygulanır — kullanıcı sonucu ekranda görür.
  useEffect(() => {
    const applyAgentFilters = (req: InventoryFilterRequest) => {
      if (req.clearAll) {
        resetAllFilters();
      }
      if (req.searchText !== undefined) {
        setSearchTerm(req.searchText);
        setDebouncedSearchTerm(req.searchText); // debounce beklemeden hemen ara
      }
      if (req.categoryId !== undefined) {
        setSelectedCategory(req.categoryId ? categories.find((c) => c.id === req.categoryId) ?? null : null);
      }
      if (req.brandId !== undefined) {
        setSelectedBrand(req.brandId ? brands.find((b) => b.id === req.brandId) ?? null : null);
      }
      if (req.warehouseId !== undefined) {
        setSelectedWarehouse(req.warehouseId ? warehouses.find((w) => w.id === req.warehouseId) ?? null : null);
      }
      if (req.vatRate !== undefined) setSelectedVatRate(req.vatRate);
      if (req.onlyActive !== undefined) setHideInactiveItems(req.onlyActive);
      if (req.hideZeroPrice !== undefined) setHideZeroPrice(req.hideZeroPrice);
      // ✅ Gelişmiş filtreler
      if (req.branchId !== undefined) {
        setSelectedBranch(req.branchId ? branches.find((b) => String(b.id) === String(req.branchId)) ?? null : null);
      }
      if (req.hideZeroStock !== undefined) {
        setHideEmptyStock(req.hideZeroStock);
        localStorage.setItem(HIDE_EMPTY_STOCK_KEY, JSON.stringify(req.hideZeroStock));
      }
      if (req.ecommerce !== undefined) setEcommerceFilter(req.ecommerce);
      if (req.onlyWithoutImage !== undefined) setOnlyWithoutImage(!!req.onlyWithoutImage);
      if (req.minSalePrice !== undefined) setMinSalePrice(req.minSalePrice);
      if (req.maxSalePrice !== undefined) setMaxSalePrice(req.maxSalePrice);
      if (req.minQuantity !== undefined) setMinQuantity(req.minQuantity);
      if (req.maxQuantity !== undefined) setMaxQuantity(req.maxQuantity);
      if (req.createdFrom !== undefined) setCreatedFrom(req.createdFrom);
      if (req.createdTo !== undefined) setCreatedTo(req.createdTo);
      setPage(0);
      setShowFilters(true); // kullanıcı hangi filtrelerin uygulandığını görsün
    };

    // Sayfa yeni açıldıysa navigate + dispatch yarışı: bekleyen istek mount'ta uygulanır
    const pending = consumePendingInventoryFilters();
    if (pending) applyAgentFilters(pending);

    const handler = (e: Event) => {
      consumePendingInventoryFilters(); // canlı uygulandı, bekleyen bayatlamasın
      applyAgentFilters((e as CustomEvent<InventoryFilterRequest>).detail);
    };
    window.addEventListener(INVENTORY_FILTERS_EVENT, handler);
    return () => window.removeEventListener(INVENTORY_FILTERS_EVENT, handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, brands, warehouses, branches, resetAllFilters]);

  // Barkod okuma modalı için state
  const [isBarcodeScannerOpen, setIsBarcodeScannerOpen] = useState<boolean>(false);

  // 3 nokta menüsü için state
  const [anchorElFeatures, setAnchorElFeatures] = useState<null | HTMLElement>(null);
  const [isInventoryInfoModalOpen, setIsInventoryInfoModalOpen] = useState<boolean>(false);
  
  const handleOpenFeaturesMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElFeatures(event.currentTarget);
  };
  
  const handleCloseFeaturesMenu = () => {
    setAnchorElFeatures(null);
  };

  // Toplu işlem seçim modalı için state (mobil)
  const [isBulkActionSelectionModalOpen, setIsBulkActionSelectionModalOpen] = useState<boolean>(false);
  const [selectedBulkAction, setSelectedBulkAction] = useState<string | null>(null);
  
  // Deposuz ürün uyarısı için state
  const [showWarehouseWarning, setShowWarehouseWarning] = useState<boolean>(false);
  const [itemsWithoutWarehouseCount, setItemsWithoutWarehouseCount] = useState<number>(0);

  // N11 Log kaydetme fonksiyonu
  const saveN11Log = (logEntry: {
    type: 'price-stock-update' | 'product-create' | 'product-save';
    productName: string;
    stockCode: string;
    taskId: number;
    status: 'IN_QUEUE' | 'PROCESSED' | 'REJECT';
    operation: string;
    details: any;
  }) => {
    if (!companyId) return;

    try {
      const storageKey = `n11_logs_${companyId}`;
      const existingLogs = localStorage.getItem(storageKey);
      const logs = existingLogs ? JSON.parse(existingLogs) : [];

      const newLog = {
        id: `${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        timestamp: new Date().toISOString(),
        ...logEntry
      };

      logs.unshift(newLog); // En yeni log en üstte

      // Maksimum 100 log tut
      if (logs.length > 100) {
        logs.splice(100);
      }

      localStorage.setItem(storageKey, JSON.stringify(logs));
    } catch (error) {
      // Log kaydetme hatası - sessizce yoksay
    }
  };

  // E-ticaret durumlarını yükle - InventoryItems tablosunda IsEcommerce kolonu mevcut, Firebase'e gerek yok
  const loadEcommerceStatuses = async (_companyId: string) => {
    // InventoryItems tablosundaki IsEcommerce alanı kullanılıyor, ayrı bir Firebase çağrısına gerek yok
    setEcommerceStatuses({});
  };

  // N11 entegrasyon durumunu kontrol et (SQL'den - .NET API)
  const loadN11Integration = async (companyId: string) => {
    try {
      console.log('🔄 [N11 INTEGRATION] SQL API\'den N11 entegrasyonu kontrol ediliyor...');
      
      const response = await mainApi.get(`/api/platformintegration/list?companyId=${encodeURIComponent(companyId)}`);
      
      const result = response.data;
      
      if (!result.success || !result.data) {
        setN11IntegrationActive(false);
        return;
      }
      
      // N11 entegrasyonunu bul
      const n11Integration = result.data.find((integration: any) => 
        integration.platform?.toLowerCase() === 'n11'
      );
      
      if (!n11Integration) {
        console.log('⚠️ [N11 INTEGRATION] N11 entegrasyonu bulunamadı');
        setN11IntegrationActive(false);
        return;
      }
      
      // N11 entegrasyonu aktif mi kontrol et (apiKey ve apiSecret var mı ve enabled mi?)
      const isActive = !!(n11Integration.isEnabled && n11Integration.apiKey && n11Integration.apiSecret);
      console.log(`✅ [N11 INTEGRATION] N11 entegrasyonu: ${isActive ? 'Aktif' : 'Pasif'}`);
      setN11IntegrationActive(isActive);
    } catch (error) {
      console.error('❌ [N11 INTEGRATION] Hata:', error);
      setN11IntegrationActive(false);
    }
  };

  // Menü açma/kapama işlevleri
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, itemId: string) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setMenuItemId(itemId);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuItemId(null);
  };

  // Menüden stok ayrıntıları seçeneği
  const handleMenuDetails = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        setSelectedItem(item);
        setIsDetailModalOpen(true);
      }
    }
    handleMenuClose();
  };

  // Menüden stok hareketi seçeneği
  const handleMenuStockMovement = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        setSelectedItem(item);
        setSelectedField('quantity'); // Stok hareketi için field'ı quantity yap
        setIsUpdateModalOpen(true);
      }
    }
    handleMenuClose();
  };

  // Menüden stok hareket raporu seçeneği
  const handleMenuStockMovementsReport = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        navigate('/accounting/inventory/movements', { 
          state: { 
            searchTerm: item.name 
          } 
        });
      }
    }
    handleMenuClose();
  };

  // Fiyat Değişim Logları sayfasına gitme
  const handlePriceChangeLogs = () => {
    navigate('/fiyat-degisim-loglari');
  };

  // Menüden düzenleme seçeneği
  const handleMenuEdit = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        handleEditItem(item);
      }
    }
    handleMenuClose();
  };

  // ✅ YENİ: Menüden şube fiyatı seçeneği
  const handleOpenBranchPrice = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        setBranchPriceSelectedItem(item);
        setIsBranchPriceModalOpen(true);
      }
    }
    handleMenuClose();
  };

  // ✅ YENİ: Menüden basit üretim reçetesi seçeneği
  const handleOpenRecipeModal = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        setRecipeSelectedItem(item);
        setIsRecipeModalOpen(true);
      }
    }
    handleMenuClose();
  };

  // ✅ YENİ: Menüden menü tanımları seçeneği
  const handleOpenProductMenuModal = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        setProductMenuSelectedItem(item);
        setIsProductMenuModalOpen(true);
      }
    }
    handleMenuClose();
  };

  // ✅ YENİ: Menüden varyantlari yonet secenegi (frontend iskelet)
  const handleOpenVariantManagementModal = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        setVariantManagementSelectedItem(item);
        setIsVariantManagementModalOpen(true);
      }
    }
    handleMenuClose();
  };

  // Menüden silme seçeneği
  const handleMenuDelete = () => {
    if (menuItemId) {
      const item = inventoryItems.find(item => item.id === menuItemId);
      if (item) {
        handleOpenDeleteDialog(item);
      }
    }
    handleMenuClose();
  };

  const handleOpenForEcommerce = async () => {
    if (menuItemId && companyId) {
      try {
        const item = inventoryItems.find(item => item.id === menuItemId);
        if (!item) {
          setError('Ürün bulunamadı');
          return;
        }

        // Ürünün şu anki e-ticaret durumunu kontrol et
        const isCurrentlyActive = ecommerceStatuses[menuItemId] === true;
        
        // EcommerceService kullanarak durumu toggle et
        const result = await EcommerceService.toggleEcommerceStatus(
          companyId,
          menuItemId,
          item,
          isCurrentlyActive
        );

        // ✅ State'de isEcommerce durumunu güncelle (UI'da anında görünsün)
        setInventoryItems(prevItems =>
          prevItems.map(i =>
            i.id === menuItemId
              ? { ...i, isEcommerce: result.newStatus }
              : i
          )
        );

        setError(null);
        showSnackbar(result.message, 'success');
        
        // Arka planda liste yenileme
        setTimeout(() => {
          handleRefreshInventory();
        }, 1000);
      } catch (error) {
        console.error('E-ticaret durumu değiştirme hatası:', error);
        const errorMessage = error instanceof Error ? error.message : 'E-ticaret durumu değiştirilirken bir hata oluştu';
        setError(errorMessage);
        showSnackbar(errorMessage, 'error');
      }
    }
    handleMenuClose();
  };

  // Toplu e-ticarete açma işlemini başlat

  // ✅ YENİ: Resim ikonuna/resme tıklandığında çağrılır
  const handleImageClick = (item: InventoryItem, event: React.MouseEvent<HTMLElement>, hasImage: boolean) => {
    setSelectedImageItem(item);
    
    if (hasImage) {
      // Resim varsa menü göster (Resmi Gör / Resmi Güncelle)
      setImageMenuAnchor(event.currentTarget);
    } else {
      // Resim yoksa direkt resim seçme modalı aç
      setIsImageLibraryModalOpen(true);
    }
  };

  // ✅ YENİ: Resim menüsünü kapat
  const handleImageMenuClose = () => {
    setImageMenuAnchor(null);
  };

  // ✅ YENİ: Resmi Gör seçeneği
  const handleViewImage = () => {
    setImageMenuAnchor(null);
    setIsImageViewModalOpen(true);
  };

  // ✅ YENİ: Resmi Güncelle seçeneği - mevcut resmi kaldırıp yeni seçim için modalı aç
  const handleEditImage = () => {
    setImageMenuAnchor(null);
    setIsImageLibraryModalOpen(true);
  };

  // ✅ YENİ: Resmi Kaldır - önce onay iste
  const handleRemoveImageRequest = () => {
    setImageMenuAnchor(null);
    setIsRemoveImageConfirmOpen(true);
  };

  // ✅ YENİ: Resmi kaldırmayı onayla (imageUrl null olur)
  const confirmRemoveImage = async () => {
    if (!selectedImageItem) return;
    try {
      setIsRemovingImage(true);
      await saveImages(selectedImageItem, []);
    } finally {
      setIsRemovingImage(false);
      setIsRemoveImageConfirmOpen(false);
    }
  };

  // Resim görüntüleme modalını kapat
  const handleCloseImageViewModal = () => {
    setIsImageViewModalOpen(false);
  };

  // Resim modalını aç (eski fonksiyon - uyumluluk için)
  const handleOpenImageLibraryModal = (item: InventoryItem) => {
    setSelectedImageItem(item);
    setIsImageLibraryModalOpen(true);
  };

  // Resim modalını kapat
  const handleCloseImageLibraryModal = () => {
    setIsImageLibraryModalOpen(false);
    setSelectedImageItem(null);
  };

  // Kütüphaneden seçilen resimleri kaydet

  // Toplu resim silme işlemini başlat
  const handleBulkDeleteImages = () => {
    if (selectedItems.length > 0) {
      bulkOps.setShowBulkDeleteImagesDialog(true);
    } else {
      setError('Lütfen resimlerini silmek için en az bir ürün seçin');
    }
  };

  // Toplu resim silme işlemini onayla ve gerçekleştir
  const confirmBulkDeleteImages = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkDeleteImagesDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkDeleteImages(selectedItems);
      
      bulkOps.setShowBulkDeleteImagesDialog(false);
      
      // State'i de anında güncelleyelim
      setInventoryItems(prevItems =>
          prevItems.map(item =>
              selectedItems.includes(item.id) ? { ...item, imageUrl: null } : item
          )
      );
      
      setSelectedItems([]);
      setError(null);
    } catch (error) {
      console.error("Toplu resim silme sırasında hata oluştu:", error);
      setError('Toplu resim silme işlemi sırasında bir hata oluştu');
    }
  };

  // Toplu resim silme işlemini iptal et
  const cancelBulkDeleteImages = () => {
    bulkOps.setShowBulkDeleteImagesDialog(false);
  };

  // ✅ Server-side sıralama isteği işleyici
  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0); // Sıralama değiştiğinde ilk sayfaya dön
  };

  // Sıralamayı sıfırla işleyici
  const handleResetSort = () => {
    setOrderBy(null);
    setPage(0);
  };

  // Snackbar fonksiyonları
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  // ✅ Value Update Hook (showSnackbar ve handleCloseUpdateModal tanımlandıktan sonra)
  const valueUpdateHook = useInventoryValueUpdate({
    companyId,
    warehouses,
    n11IntegrationActive,
    getCategoryName,
    getBrandName,
    showSnackbar,
    setInventoryItems,
    onCloseModal: handleCloseUpdateModal,
  });

  // ✅ Hook'tan state'leri al
  const isUpdating = valueUpdateHook.isUpdating;
  const isN11Syncing = valueUpdateHook.isN11Syncing;

  // ✅ Excel Export Hook
  const { loadingExport, exportToExcel } = useInventoryExcelExport({
    companyId,
    searchTerm,
    selectedCategory,
    selectedBrand,
    selectedWarehouse,
    selectedBranch,
    hideEmptyStock,
    hideInactiveItems,
    hideZeroPrice,
    minSalePrice,
    maxSalePrice,
    minQuantity,
    maxQuantity,
    createdFrom,
    createdTo,
    ecommerceFilter,
    onlyWithoutImage,
    getCategoryName,
    getBrandName,
    getWarehouseName,
    getBranchName,
    getCurrencySymbol,
    showSnackbar,
    setError,
  });

  // ✅ N11 Product Send Hook
  const { n11SendingProduct, sendProductToN11 } = useN11ProductSend({
    companyId,
    getBrandName,
    showSnackbar,
    saveN11Log,
  });

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  // N11 ürün yükleme modalını aç
  const handleOpenN11UploadModal = () => {
    if (!n11IntegrationActive) {
      showSnackbar('Lütfen N11 entegrasyon bilgilerini giriniz', 'warning');
      return;
    }
    setIsN11UploadModalOpen(true);
  };

  // N11 ürün yükleme modalını kapat
  const handleCloseN11UploadModal = () => {
    setIsN11UploadModalOpen(false);
  };

  // Mevcut ürünü N11'e gönder - kategori seçim modal'ını aç
  const handleSendToN11 = () => {
    if (!menuItemId || !companyId) {
      showSnackbar('Ürün bilgileri eksik', 'error');
      handleMenuClose();
      return;
    }

    // N11 entegrasyonu aktif mi kontrol et
    if (!n11IntegrationActive) {
      showSnackbar('Lütfen N11 entegrasyon bilgilerini giriniz', 'warning');
      handleMenuClose();
      return;
    }

    const item = inventoryItems.find(item => item.id === menuItemId);
    if (!item) {
      showSnackbar('Ürün bulunamadı', 'error');
      handleMenuClose();
      return;
    }

    // Kategori seçim modal'ını aç
    setSelectedItemForN11(item);
    setIsN11CategoryModalOpen(true);
    handleMenuClose();
  };

  // Kategori seçimi tamamlandığında ürünü N11'e gönder
  const handleN11CategoryConfirm = async (categoryId: string, shipmentTemplate?: string) => {
    if (!selectedItemForN11) {
      setIsN11CategoryModalOpen(false);
      return;
    }

    const success = await sendProductToN11(selectedItemForN11, categoryId, shipmentTemplate);
    
    if (success) {
      setIsN11CategoryModalOpen(false);
      setSelectedItemForN11(null);
    }
  };

  // N11 kategori seçim modal'ını kapat
  const handleN11CategoryModalClose = () => {
    if (!n11SendingProduct) {
      setIsN11CategoryModalOpen(false);
      setSelectedItemForN11(null);
    }
  };

  // ✅ Toplu QR Menü açma - Hook'tan gelen fonksiyonları kullan
  const handleBulkToggleQRMenu = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen QR Menü\'de açmak için en az bir ürün seçin', 'warning');
      return;
    }
    
    // Seçili ürünleri al ve QR Menü durumlarına göre ayır
    const selectedProducts = inventoryItems.filter(item => selectedItems.includes(item.id));
    const alreadyOpenItems = selectedProducts.filter(item => item.isQRMenu === true);
    const closedItems = selectedProducts.filter(item => item.isQRMenu !== true);
    
    // Eğer tüm ürünler zaten açıksa uyarı göster
    if (closedItems.length === 0) {
      showSnackbar('Seçili tüm ürünler zaten QR Menü\'de açık!', 'info');
      return;
    }
    
    // Zaten açık olan ürün sayısını results'a ekle
    bulkOps.setBulkQRMenuResults({
      success: 0, 
      failed: 0, 
      total: closedItems.length,
      alreadyOpen: alreadyOpenItems.length,
      alreadyOpenNames: alreadyOpenItems.map(i => i.name)
    });
    bulkOps.setBulkQRMenuProgress(0);
    bulkOps.setBulkQRMenuCurrentItem('');
    bulkOps.setShowBulkQRMenuModal(true);
  };

  // ✅ Toplu QR Menü açma işlemini onayla - Hook kullan
  const confirmBulkToggleQRMenu = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkQRMenuModal(false);
      return;
    }

    try {
      const result = await bulkOps.executeBulkToggleQRMenu(
        selectedItems,
        inventoryItems,
        (itemId, updates) => {
        setInventoryItems(prevItems =>
            prevItems.map(i => (i.id === itemId ? { ...i, ...updates } : i))
          );
        }
      );
      
      // Sonuç mesajları
      if (result.success > 0) {
        showSnackbar(`${result.success} ürün başarıyla QR Menü'de açıldı!`, 'success');
      }
      
      if (result.failed > 0) {
        showSnackbar(`${result.failed} ürün işlenirken hata oluştu`, 'warning');
      }

      // Seçimleri temizle
      setSelectedItems([]);
      
      // Modalı kapat
      setTimeout(() => {
        bulkOps.setShowBulkQRMenuModal(false);
        bulkOps.setBulkQRMenuProgress(0);
        bulkOps.setBulkQRMenuResults({success: 0, failed: 0, total: 0});
        bulkOps.setBulkQRMenuCurrentItem('');
      }, 500);
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Toplu QR Menü açma sırasında bir hata oluştu', 'error');
    }
  };

  // ✅ Toplu QR Menü modalını kapat
  const cancelBulkToggleQRMenu = () => {
    if (!bulkOps.bulkQRMenuProcessing) {
      bulkOps.setShowBulkQRMenuModal(false);
      bulkOps.setBulkQRMenuProgress(0);
      bulkOps.setBulkQRMenuResults({success: 0, failed: 0, total: 0});
      bulkOps.setBulkQRMenuCurrentItem('');
    }
  };

  // ✅ Toplu Kiosk açma - Hook'tan gelen fonksiyonları kullan
  const handleBulkToggleKiosk = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen Kiosk\'ta açmak için en az bir ürün seçin', 'warning');
      return;
    }
    
    // Seçili ürünleri al ve Kiosk durumlarına göre ayır
    const selectedProducts = inventoryItems.filter(item => selectedItems.includes(item.id));
    const alreadyOpenItems = selectedProducts.filter(item => item.isKiosk === true);
    const closedItems = selectedProducts.filter(item => item.isKiosk !== true);
    
    // Eğer tüm ürünler zaten açıksa uyarı göster
    if (closedItems.length === 0) {
      showSnackbar('Seçili tüm ürünler zaten Kiosk\'ta açık!', 'info');
      return;
    }
    
    // Zaten açık olan ürün sayısını results'a ekle
    bulkOps.setBulkKioskResults({
      success: 0, 
      failed: 0, 
      total: closedItems.length,
      alreadyOpen: alreadyOpenItems.length,
      alreadyOpenNames: alreadyOpenItems.map(i => i.name)
    });
    bulkOps.setBulkKioskProgress(0);
    bulkOps.setBulkKioskCurrentItem('');
    bulkOps.setShowBulkKioskModal(true);
  };

  // ✅ Toplu Kiosk açma işlemini onayla - Hook kullan
  const confirmBulkToggleKiosk = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkKioskModal(false);
      return;
    }

    try {
      const result = await bulkOps.executeBulkToggleKiosk(
        selectedItems,
        inventoryItems,
        (itemId, updates) => {
        setInventoryItems(prevItems =>
            prevItems.map(i => (i.id === itemId ? { ...i, ...updates } : i))
          );
        }
      );
      
      // Sonuç mesajları
      if (result.success > 0) {
        showSnackbar(`${result.success} ürün başarıyla Kiosk'ta açıldı!`, 'success');
      }
      
      if (result.failed > 0) {
        showSnackbar(`${result.failed} ürün işlenirken hata oluştu`, 'warning');
      }

      // Seçimleri temizle
      setSelectedItems([]);
      
      // Modalı kapat
      setTimeout(() => {
        bulkOps.setShowBulkKioskModal(false);
        bulkOps.setBulkKioskProgress(0);
        bulkOps.setBulkKioskResults({success: 0, failed: 0, total: 0});
        bulkOps.setBulkKioskCurrentItem('');
      }, 500);
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Toplu Kiosk açma sırasında bir hata oluştu', 'error');
    }
  };

  // ✅ Toplu Kiosk modalını kapat
  const cancelBulkToggleKiosk = () => {
    if (!bulkOps.bulkKioskProcessing) {
      bulkOps.setShowBulkKioskModal(false);
      bulkOps.setBulkKioskProgress(0);
      bulkOps.setBulkKioskResults({success: 0, failed: 0, total: 0});
      bulkOps.setBulkKioskCurrentItem('');
    }
  };

  // Manuel stok yenileme fonksiyonu - ✅ YENİ: Server-side pagination ile
  const handleRefreshInventory = async () => {
    if (!companyId) return;
    
    setRefreshing(true);
    try {
      await loadInventoryItemsPaged(
        companyId,
        page + 1,
        rowsPerPage,
        debouncedSearchTerm || undefined,
        selectedCategory?.id || undefined,
        selectedBrand?.id || undefined,
        selectedWarehouse?.id || undefined,
        hideInactiveItems ? true : undefined,
        undefined,
        orderBy ? undefined : sortNewestFirst,
        hideZeroPrice ? true : undefined,
        selectedVatRate ?? undefined,
        selectedDepartmentNo ?? undefined,
        orderBy || undefined,
        orderBy ? order : undefined,
        effectiveExcludeVariantChildren
      );
      showSnackbar('Stok listesi başarıyla yenilendi!', 'success');
    } catch (error) {
      showSnackbar('Stok listesi yenilenirken bir hata oluştu', 'error');
    } finally {
      setRefreshing(false);
    }
  };

  // ✅ Bulk Operations Handlers Hook - handleRefreshInventory'den sonra!
  const bulkHandlers = useBulkOperationsHandlers({
    companyId,
    inventoryItems,
    warehouses,
    selectedItems,
    bulkOps,
    showSnackbar,
    setError,
    setSelectedItems,
    setInventoryItems,
    handleRefreshInventory,
  });

  // ✅ Image Update Hook - handleRefreshInventory'den sonra!
  const { saveImages } = useInventoryImageUpdate({
    companyId,
    showSnackbar,
    setError,
    setInventoryItems,
    handleRefreshInventory,
  });

  // Kütüphaneden seçilen resimleri kaydet
  const handleSaveImages = async (selectedUrls: string[]) => {
    if (!selectedImageItem) return;
    await saveImages(selectedImageItem, selectedUrls);
  };

  // Barkod okuma modalını aç
  const handleOpenBarcodeScanner = () => {
    setIsBarcodeScannerOpen(true);
  };

  // Barkod okuma modalını kapat
  const handleCloseBarcodeScanner = () => {
    setIsBarcodeScannerOpen(false);
  };

  // Barkod okunduğunda sadece arama filtresine yaz
  const handleBarcodeScanned = (barcode: string) => {
    // Modal'ı kapat
    setIsBarcodeScannerOpen(false);
    
    // Sadece arama alanına barkodu yaz
    setSearchTerm(barcode);
    
    // Bilgilendirme
    showSnackbar(`🔍 Barkod "${barcode}" aranıyor...`, 'info');
  };

  // ✅ Item Actions Hook (QR Menu, Kiosk, Activate) - handleRefreshInventory'den sonra!
  const { toggleQRMenu, toggleKiosk, activateItem } = useInventoryItemActions({
    companyId,
    inventoryItems,
    showSnackbar,
    setInventoryItems,
    handleRefreshInventory,
  });

  // QR Menü durumunu değiştir (Aç/Kapat)
  const handleToggleQRMenu = async () => {
    if (!menuItemId) return;
    await toggleQRMenu(menuItemId);
      handleMenuClose();
  };

  // Kiosk'a toggle fonksiyonu (QR Menu ile aynı mantık)
  // Kiosk durumunu değiştir (Aç/Kapat)
  const handleToggleKiosk = async () => {
    if (!menuItemId) return;
    await toggleKiosk(menuItemId);
      handleMenuClose();
  };

  // QR Menü'ye stok gönderme fonksiyonu
  const handleSendToQRMenu = async () => {
    if (!menuItemId || !companyId) {
      showSnackbar('Ürün bilgileri eksik', 'error');
      handleMenuClose();
      return;
    }

    const item = inventoryItems.find(item => item.id === menuItemId);
    if (!item) {
      showSnackbar('Ürün bulunamadı', 'error');
      handleMenuClose();
      return;
    }

    try {
      showSnackbar('Ürün QR Menü sistemine gönderiliyor...', 'info');
      
      // IntegrationService kullanarak gönder (SQL API)
      await IntegrationService.sendToQRMenu(companyId, [item]);
      
      // ✅ State'de isQRMenu durumunu güncelle (UI'da anında görünsün)
      setInventoryItems(prevItems =>
        prevItems.map(i =>
          i.id === menuItemId
            ? { ...i, isQRMenu: true, qrMenuSentAt: new Date().toISOString() }
            : i
        )
      );
      
      showSnackbar(`${item.name} başarıyla QR Menü sistemine gönderildi!`, 'success');
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'QR Menü gönderme sırasında bir hata oluştu', 'error');
    } finally {
      handleMenuClose();
    }
  };

  // Kiosk'a stok gönderme fonksiyonu
  const handleSendToKiosk = async () => {
    if (!menuItemId || !companyId) {
      showSnackbar('Ürün bilgileri eksik', 'error');
      handleMenuClose();
      return;
    }

    const item = inventoryItems.find(item => item.id === menuItemId);
    if (!item) {
      showSnackbar('Ürün bulunamadı', 'error');
      handleMenuClose();
      return;
    }

    try {
      showSnackbar('Ürün Kiosk sistemine gönderiliyor...', 'info');
      
      // IntegrationService kullanarak gönder
      await IntegrationService.sendToKiosk(companyId, [item]);
      
      // ✅ State'de isKiosk durumunu güncelle (UI'da anında görünsün)
      setInventoryItems(prevItems =>
        prevItems.map(i =>
          i.id === menuItemId
            ? { ...i, isKiosk: true, kioskSentAt: new Date().toISOString() }
            : i
        )
      );
      
      showSnackbar(`${item.name} başarıyla Kiosk sistemine gönderildi!`, 'success');
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Kiosk gönderme sırasında bir hata oluştu', 'error');
    } finally {
      handleMenuClose();
    }
  };

  // Merkez Depo yoksa otomatik oluştur
  // Not: warehouses AuthContext'ten async yüklenir, ilk render'da [] olur.
  // Bu yüzden warehouses.length > 0 olana kadar veya yeterli süre geçene kadar bekliyoruz.
  const warehouseCreateAttempted = useRef(false);
  useEffect(() => {
    if (!companyId || warehouseCreateAttempted.current) return;
    if (warehouses.length > 0) {
      warehouseCreateAttempted.current = true;
    }
  }, [companyId, warehouses]);

  // Deposuz ürünleri kontrol et
  useEffect(() => {
    if (inventoryItems.length > 0 && warehouses.length > 0) {
      const itemsWithoutWarehouse = inventoryItems.filter(item => 
        !item.warehouseStocks || item.warehouseStocks.length === 0
      );
      
      if (itemsWithoutWarehouse.length > 0) {
        setShowWarehouseWarning(true);
        setItemsWithoutWarehouseCount(itemsWithoutWarehouse.length);
      } else {
        setShowWarehouseWarning(false);
        setItemsWithoutWarehouseCount(0);
      }
    }
  }, [inventoryItems, warehouses]);

  // ✅ Toplu depo atama - Hook'tan gelen fonksiyonları kullan
  const handleBulkAssignWarehouse = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen depoya atamak için en az bir ürün seçin', 'warning');
      return;
    }
    
    // Seçili ürünlerden deposuz olanları filtrele
    const itemsToAssign = inventoryItems.filter(item => 
      selectedItems.includes(item.id) && 
      (!item.warehouseStocks || item.warehouseStocks.length === 0)
    );
    
    if (itemsToAssign.length === 0) {
      showSnackbar('Seçili tüm ürünlerin zaten deposu var!', 'info');
      return;
    }
    
    if (itemsToAssign.length < selectedItems.length) {
      showSnackbar(
        `${selectedItems.length - itemsToAssign.length} ürünün zaten deposu var. Sadece ${itemsToAssign.length} ürün atanacak.`,
        'info'
      );
    }
    
    bulkOps.setShowBulkWarehouseAssignModal(true);
  };

  // ✅ Toplu depo atama işlemini onayla - Hook kullan
  const confirmBulkAssignWarehouse = async (warehouseId: string) => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkWarehouseAssignModal(false);
      return;
    }

    try {
      // Seçili ürünlerden deposuz olanları filtrele
      const itemsToAssign = inventoryItems.filter(item => 
        selectedItems.includes(item.id) && 
        (!item.warehouseStocks || item.warehouseStocks.length === 0)
      );
      
      if (itemsToAssign.length === 0) {
        showSnackbar('Atanacak deposuz ürün bulunamadı', 'warning');
        bulkOps.setShowBulkWarehouseAssignModal(false);
        return;
      }
      
      const result = await bulkOps.executeBulkAssignWarehouse(
        itemsToAssign.map(item => item.id),
        warehouseId
      );
      
      // State'i güncelle
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          itemsToAssign.some(assignedItem => assignedItem.id === item.id)
            ? {
                ...item,
                warehouseStocks: [{
                  id: '',
                  productId: item.id,
                  warehouseId: warehouseId,
                  warehouseName: warehouses.find(w => w.id === warehouseId)?.name,
                  quantity: 0,
                  createdAt: new Date().toISOString(),
                  updatedAt: new Date().toISOString()
                }]
              }
            : item
        )
      );
      
      showSnackbar(`${result.count} ürün başarıyla depoya atandı!`, 'success');
      
      // Seçimleri temizle
      setSelectedItems([]);
      bulkOps.setShowBulkWarehouseAssignModal(false);
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
      
    } catch (error) {
      showSnackbar(error instanceof Error ? error.message : 'Toplu depo atama sırasında bir hata oluştu', 'error');
    }
  };

  // ✅ Toplu depo atama modalını kapat
  const cancelBulkAssignWarehouse = () => {
    if (!bulkOps.bulkWarehouseAssignProcessing) {
      bulkOps.setShowBulkWarehouseAssignModal(false);
    }
  };

  // ============== TOPLU KDV ORANI GÜNCELLEME ==============
  
  // Toplu KDV oranı güncelleme işlemini başlat
  const handleBulkUpdateVatRate = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen KDV oranını güncellemek için en az bir ürün seçin', 'warning');
      return;
    }
    bulkOps.setShowBulkVatRateDialog(true);
  };

  // Toplu KDV oranı güncelleme işlemini onayla ve gerçekleştir
  const confirmBulkUpdateVatRate = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkVatRateDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkVatRateUpdate(selectedItems);
      
      // ✅ State'de vatRate durumunu güncelle (UI'da anında görünsün)
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id)
            ? { ...item, vatRate: bulkOps.bulkVatRate }
            : item
        )
      );
      
      bulkOps.setShowBulkVatRateDialog(false);
      setSelectedItems([]);
      
      showSnackbar(`${selectedItems.length} ürünün KDV oranı %${bulkOps.bulkVatRate} olarak güncellendi`, 'success');
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      showSnackbar('Toplu KDV güncelleme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  // Toplu KDV oranı güncelleme modalını kapat
  const cancelBulkUpdateVatRate = () => {
    bulkOps.setShowBulkVatRateDialog(false);
  };

  // ============== TOPLU ÖKC (DEPARTMAN NO) GÜNCELLEME ==============
  
  // Toplu ÖKC güncelleme işlemini başlat
  const handleBulkUpdateDepartmentNo = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen ÖKC değerini güncellemek için en az bir ürün seçin', 'warning');
      return;
    }
    bulkOps.setShowBulkDepartmentNoDialog(true);
  };

  // Toplu ÖKC güncelleme işlemini onayla ve gerçekleştir
  const confirmBulkUpdateDepartmentNo = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkDepartmentNoDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkDepartmentNoUpdate(selectedItems);
      
      // ✅ State'de departmentNo durumunu güncelle (UI'da anında görünsün)
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id)
            ? { ...item, departmentNo: bulkOps.bulkDepartmentNo }
            : item
        )
      );
      
      bulkOps.setShowBulkDepartmentNoDialog(false);
      setSelectedItems([]);
      
      showSnackbar(`${selectedItems.length} ürünün ÖKC değeri ${bulkOps.bulkDepartmentNo} olarak güncellendi`, 'success');
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      showSnackbar('Toplu ÖKC güncelleme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  // Toplu ÖKC güncelleme modalını kapat
  const cancelBulkUpdateDepartmentNo = () => {
    bulkOps.setShowBulkDepartmentNoDialog(false);
  };

  // ============== TOPLU KDV + ÖKC GÜNCELLEME ==============
  
  // Toplu KDV + ÖKC güncelleme işlemini başlat
  const handleBulkUpdateVatAndDept = () => {
    if (selectedItems.length === 0) {
      showSnackbar('Lütfen KDV ve ÖKC değerlerini güncellemek için en az bir ürün seçin', 'warning');
      return;
    }
    bulkOps.setShowBulkVatAndDeptDialog(true);
  };

  // Toplu KDV + ÖKC güncelleme işlemini onayla ve gerçekleştir
  const confirmBulkUpdateVatAndDept = async () => {
    if (!companyId || selectedItems.length === 0) {
      bulkOps.setShowBulkVatAndDeptDialog(false);
      return;
    }

    try {
      await bulkOps.executeBulkVatAndDeptUpdate(selectedItems);
      
      // ✅ State'de hem vatRate hem departmentNo durumunu güncelle (UI'da anında görünsün)
      setInventoryItems(prevItems =>
        prevItems.map(item =>
          selectedItems.includes(item.id)
            ? { ...item, vatRate: bulkOps.bulkVatRate, departmentNo: bulkOps.bulkDepartmentNo }
            : item
        )
      );
      
      bulkOps.setShowBulkVatAndDeptDialog(false);
      setSelectedItems([]);
      
      showSnackbar(`${selectedItems.length} ürünün KDV oranı %${bulkOps.bulkVatRate} ve ÖKC değeri ${bulkOps.bulkDepartmentNo} olarak güncellendi`, 'success');
      
      // Arka planda liste yenileme
      setTimeout(() => {
        handleRefreshInventory();
      }, 1000);
    } catch (error) {
      showSnackbar('Toplu KDV+ÖKC güncelleme işlemi sırasında bir hata oluştu', 'error');
    }
  };

  // Toplu KDV + ÖKC güncelleme modalını kapat
  const cancelBulkUpdateVatAndDept = () => {
    bulkOps.setShowBulkVatAndDeptDialog(false);
  };

  // Otomatik olarak tüm deposuz ürünleri Merkez Depo'ya ata
  const handleAutoAssignToMainWarehouse = async () => {
    if (!companyId) return;
    
    // Merkez Depo'yu bul
    const centralWarehouse = warehouses.find(w => 
      w.name.toLowerCase().includes('merkez') ||
      w.name.toLowerCase() === 'merkez depo' ||
      w.name.toLowerCase() === 'ana depo'
    );
    
    if (!centralWarehouse) {
      showSnackbar('Merkez Depo bulunamadı! Lütfen önce "Merkez Depo" adında bir depo oluşturun.', 'error');
      return;
    }
    
    // Deposuz ürünleri bul
    const itemsWithoutWarehouse = inventoryItems.filter(item => 
      !item.warehouseStocks || item.warehouseStocks.length === 0
    );
    
    if (itemsWithoutWarehouse.length === 0) {
      showSnackbar('Deposuz ürün bulunamadı', 'info');
      return;
    }
    
    try {
      const result = await InventoryService.bulkAssignWarehouse(
        companyId,
        itemsWithoutWarehouse.map(item => item.id),
        centralWarehouse.id,
        0
      );
      
      showSnackbar(`${result.count} ürün başarıyla Merkez Depo'ya atandı!`, 'success');
      
      // Uyarıyı kapat
      setShowWarehouseWarning(false);
      
      // Listeyi yenile
      handleRefreshInventory();
      
    } catch (error) {
      showSnackbar('Otomatik depo atama sırasında bir hata oluştu', 'error');
    }
  };

  // N11 ile ürün senkronizasyonu
  const syncProductWithN11 = async (item: InventoryItem, field: 'quantity' | 'salePrice', newValue: number) => {
    if (!companyId) {
      throw new Error('Şirket ID eksik');
    }

    // N11Service kullanarak senkronize et
    const result = await N11Service.syncProductWithN11(companyId, item, field, newValue);

    if (!result.success) {
      throw new Error(result.error || 'N11 senkronizasyonu başarısız');
    }

    // Log kaydet
    saveN11Log({
      type: 'price-stock-update',
      productName: item.name,
      stockCode: item.stockCode,
      taskId: result.taskId!,
      status: result.status as 'IN_QUEUE' | 'PROCESSED' | 'REJECT',
      operation: `${field === 'quantity' ? 'Miktar' : 'Fiyat'} güncelleme: ${field === 'quantity' ? `${newValue} ${item.unit}` : `${newValue} TL`}`,
      details: {
        field,
        oldValue: field === 'quantity' 
          ? (item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0) // ✅ Toplam stok
          : item.salePrice,
        newValue,
        currency: field === 'salePrice' ? (item.saleCurrency || 'TRY') : null,
        unit: field === 'quantity' ? item.unit : null,
        listPrice: field === 'salePrice' ? ((item as any).listPrice || newValue) : null
      }
    });

    // Başarı mesajı
    const successMessage = N11Service.formatSyncSuccessMessage(
      item,
      field,
      newValue,
      result.taskId!,
      result.status!,
      result.reasons
    );
    showSnackbar(successMessage, 'success');
  };

  return (
    <>
      {/* Üst Başlık */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexDirection: { xs: 'column', md: 'row' }, gap: { xs: 2, md: 0 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <InventoryIcon sx={{ fontSize: { xs: 28, md: 32 }, mr: 1.5, color: 'primary.main' }} />
          <Typography variant={isMobile ? "h6" : "h5"} component="h1" fontWeight="bold">
            {t('stock.title')}
          </Typography>
        </Box>
      </Box>

      {/* İstatistik Kartları */}
      <StatsCardsContainer loading={summaryLoading && !inventorySummary}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<CategoryIcon />}
            color={STATS_COLORS.green}
            title={formatStatsCurrency(inventorySummary?.totalStockValue ?? 0)}
            subtitle={t('stock.stats.totalPurchaseValue')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<SellIcon />}
            color={STATS_COLORS.purple}
            title={formatStatsCurrency(inventorySummary?.totalStockValueSale ?? 0)}
            subtitle={t('stock.stats.totalSaleValue')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<TrendingUpIcon />}
            color={STATS_COLORS.blue}
            title={inventorySummary?.topSellingProductName ?? '-'}
            subtitle={t('stock.stats.topSelling', { count: inventorySummary?.topSellingProductQty ?? 0 })}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCardItem
            icon={<HourglassEmptyIcon />}
            color={(inventorySummary?.idleProductCount ?? 0) > 0 ? STATS_COLORS.red : STATS_COLORS.grey}
            title={`${inventorySummary?.idleProductCount ?? 0}`}
            subtitle={t('stock.stats.idleProducts')}
          />
        </Grid>
      </StatsCardsContainer>

      {/* Akıllı arama + filtre butonu + aksiyonlar */}
      <Box sx={{ mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Arama Alanı + Filtre butonu */}
          <Grid item xs={12} md={5}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <TextField
                fullWidth
                size="small"
                placeholder={t('stock.filters.searchPlaceholder')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <Tooltip title={t('stock.filters.advancedSearchTitle')}>
                <InventoryFilterBadge count={activeFilters.length}>
                  <IconButton
                    color={showFilters || activeFilters.length > 0 ? 'primary' : 'default'}
                    onClick={() => setShowFilters(true)}
                    size="small"
                    sx={{
                      border: '1px solid',
                      borderColor: showFilters || activeFilters.length > 0 ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      width: 40,
                      height: 40,
                    }}
                  >
                    <FilterListIcon />
                  </IconButton>
                </InventoryFilterBadge>
              </Tooltip>
              {isMobile && (
                <Tooltip title={t('stock.actions.scanBarcode')}>
                  <IconButton
                    color="primary"
                    onClick={handleOpenBarcodeScanner}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      '&:hover': { bgcolor: 'primary.dark' },
                      width: 40,
                      height: 40,
                    }}
                  >
                    <CameraAltIcon />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          </Grid>

          {/* Desktop Butonlar */}
          <Grid item xs={12} md={7} sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
            <ButtonGroup>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddModal}
              >
                {t('stock.actions.newStock')}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<BoltIcon />}
                onClick={handleOpenQuickAddModal}
              >
                {t('stock.actions.quickStockEntry')}
              </Button>
              <Tooltip title={t('stock.actions.refreshList')}>
                <Button
                  variant="outlined"
                  onClick={handleRefreshInventory}
                  disabled={refreshing || loading}
                  sx={{
                    minWidth: 'auto',
                    px: 1,
                    '& .MuiButton-startIcon': {
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                      '@keyframes spin': {
                        '0%': { transform: 'rotate(0deg)' },
                        '100%': { transform: 'rotate(360deg)' }
                      }
                    }
                  }}
                >
                  <RefreshIcon />
                </Button>
              </Tooltip>
              <Button
                variant="outlined"
                onClick={handleOpenFeaturesMenu}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </ButtonGroup>
          </Grid>

          {/* Mobil Butonlar */}
          <Grid item xs={12} sx={{ display: { xs: 'grid', md: 'none' } }}>
            <Grid container spacing={1}>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddModal}
                >
                  {t('stock.actions.newStock')}
                </Button>
              </Grid>
              <Grid item xs={4}>
                <Button
                  fullWidth
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<BoltIcon />}
                  onClick={handleOpenQuickAddModal}
                >
                  {t('stock.actions.quickStock')}
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleRefreshInventory}
                  disabled={refreshing || loading}
                  size="small"
                  sx={{ minWidth: 'auto', px: 0.5 }}
                >
                  <RefreshIcon fontSize="small" />
                </Button>
              </Grid>
              <Grid item xs={2}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={handleOpenFeaturesMenu}
                  size="small"
                  sx={{ minWidth: 'auto', px: 0.5 }}
                >
                  <MoreVertIcon fontSize="small" />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {/* Aktif filtre chip'leri (popup kapalıyken de görünür) */}
        {activeFilters.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.75, mt: 1.5 }}>
            {activeFilters.map((f) => (
              <Chip
                key={f.key}
                label={f.label}
                size="small"
                onDelete={f.onDelete}
                color="primary"
                variant="outlined"
              />
            ))}
            <Button size="small" color="error" variant="text" onClick={resetAllFilters}>
              {t('stock.filters.clearAll')}
            </Button>
          </Box>
        )}
      </Box>

      {showWarehouseWarning && (
        <Alert 
          severity="warning" 
          sx={{ mb: 2 }}
          action={
              <Button
              color="inherit" 
                size="small"
              onClick={handleAutoAssignToMainWarehouse}
              startIcon={<WarehouseIcon />}
              >
              Hepsini Merkez Depo'ya Taşı
              </Button>
          }
        >
          ⚠️ <strong>{itemsWithoutWarehouseCount}</strong> ürünün deposu yok! 
          Miktar güncellemesi ve depo transferi için önce depoya atamalısınız.
        </Alert>
      )}

      {/* Ayrıntılı arama popup — seçimler "Uygula" ile commit edilir */}
      <InventoryFilters
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onApply={handleApplyFilters}
        hierarchicalCategories={hierarchicalCategories}
        categoryHasChildren={categoryHasChildren}
        brands={brands}
        warehouses={warehouses}
        branches={branches}
        locationOptions={locationOptions}
        isSmartWarehouse={isSmartWarehouse}
        applied={appliedFilterDraft}
        orderBy={orderBy}
        onResetSort={handleResetSort}
        activeFilters={activeFilters}
        onClearAllFilters={resetAllFilters}
      />

        {/* Mobil: Toplu İşlem Butonu (Filtrelerin Altında) */}
        {isMobile && bulkActionMode && (
          <MobilBulkActionButtons
            selectedBulkAction={selectedBulkAction}
            selectedItemsCount={selectedItems.length}
            onBulkDelete={bulkHandlers.handleBulkDelete}
            onBulkInactivate={bulkHandlers.handleBulkInactivate}
            onBulkPriceUpdate={bulkHandlers.handleBulkPriceUpdate}
            onBulkPurchasePriceUpdate={bulkHandlers.handleBulkPurchasePriceUpdate}
            onBulkOpenForEcommerce={bulkHandlers.handleBulkOpenForEcommerce}
            onBulkCloseForEcommerce={bulkHandlers.handleBulkCloseForEcommerce}
            onBulkToggleQRMenu={handleBulkToggleQRMenu}
            onBulkToggleKiosk={handleBulkToggleKiosk}
            onBulkDeleteImages={handleBulkDeleteImages}
            onBulkAssignWarehouse={handleBulkAssignWarehouse}
            onBulkUpdateVatRate={handleBulkUpdateVatRate}
            onBulkUpdateDepartmentNo={handleBulkUpdateDepartmentNo}
            onBulkUpdateVatAndDept={handleBulkUpdateVatAndDept}
            onDeleteAllItems={handleDeleteAllItems}
          />
        )}

      {/* Ana İçerik */}
      <Box sx={{ mb: 3 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {/* ✅ Boş liste durumu - sadece ilk yüklemede göster */}
        {!loading && filteredItems.length === 0 && (
          <Box sx={{ 
            p: 4, 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center',
            justifyContent: 'center',
            height: '400px',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf2 100%)',
            borderRadius: 2
          }}>
            <InventoryIcon 
              sx={{ 
                fontSize: 80, 
                color: 'primary.main',
                mb: 2
              }} 
            />
            
            <Typography variant="h5" component="h2" gutterBottom color="primary.main" fontWeight="bold">
              Stok Listesi
            </Typography>
            
            <Typography variant="body1" color="text.secondary">
              {searchTerm ? t('stock.empty.searchNotFound') : t('stock.empty.noRecords')}
            </Typography>
          </Box>
        )}
        
        {/* ✅ Tablo - loading overlay ile */}
        {(loading || filteredItems.length > 0) && (
          <Box sx={{ position: 'relative' }}>
            {/* Loading Overlay */}
            {loading && (
              <Box sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.7)',
                zIndex: 10,
                borderRadius: 2,
              }}>
                <CircularProgress />
              </Box>
            )}
            
            {/* Desktop: Tablo */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <InventoryTable
                paginatedItems={paginatedItems}
                hierarchicalItems={hierarchicalItems}
                visibleColumns={visibleColumns}
                bulkActionMode={bulkActionMode}
                selectedItems={selectedItems}
                onSelectItem={handleSelectItem}
                onSelectAll={handleSelectAll}
                selectedBulkAction={selectedBulkAction}
                expandedRows={expandedRows}
                onToggleRow={handleToggleRow}
                onBulkDelete={bulkHandlers.handleBulkDelete}
                onBulkInactivate={bulkHandlers.handleBulkInactivate}
                onBulkPriceUpdate={bulkHandlers.handleBulkPriceUpdate}
                onBulkPurchasePriceUpdate={bulkHandlers.handleBulkPurchasePriceUpdate}
                onBulkOpenForEcommerce={bulkHandlers.handleBulkOpenForEcommerce}
                onBulkCloseForEcommerce={bulkHandlers.handleBulkCloseForEcommerce}
                onDeleteAllItems={handleDeleteAllItems}
                onBulkDeleteImages={handleBulkDeleteImages}
                onBulkSendToQRMenu={handleBulkToggleQRMenu}
                onBulkSendToKiosk={handleBulkToggleKiosk}
                onBulkAssignWarehouse={handleBulkAssignWarehouse}
                onBulkUpdateVatRate={handleBulkUpdateVatRate}
                onBulkUpdateDepartmentNo={handleBulkUpdateDepartmentNo}
                onBulkUpdateVatAndDept={handleBulkUpdateVatAndDept}
                selectedWarehouse={selectedWarehouse}
                order={order}
                orderBy={orderBy}
                onRequestSort={(property) => handleRequestSort(property)}
                page={page}
                rowsPerPage={rowsPerPage}
                totalCount={totalCount}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                hideEmptyStock={hideEmptyStock}
                onHideEmptyStockChange={(checked) => {
                  setHideEmptyStock(checked);
                  localStorage.setItem(HIDE_EMPTY_STOCK_KEY, JSON.stringify(checked));
                }}
                hideInactiveItems={hideInactiveItems}
                onHideInactiveItemsChange={(checked) => {
                  setHideInactiveItems(checked);
                  localStorage.setItem(HIDE_INACTIVE_ITEMS_KEY, JSON.stringify(checked));
                }}
                sortNewestFirst={sortNewestFirst}
                onSortNewestFirstChange={(checked) => {
                  setSortNewestFirst(checked);
                  localStorage.setItem('INVENTORY_SORT_NEWEST_FIRST', JSON.stringify(checked));
                }}
                hideZeroPrice={hideZeroPrice}
                onHideZeroPriceChange={(checked) => {
                  setHideZeroPrice(checked);
                  localStorage.setItem('INVENTORY_HIDE_ZERO_PRICE', JSON.stringify(checked));
                }}
                showVariantChildren={showVariantChildren}
                onShowVariantChildrenChange={(checked) => {
                  setShowVariantChildren(checked);
                  localStorage.setItem('INVENTORY_SHOW_VARIANT_CHILDREN', JSON.stringify(checked));
                }}
                getCellValue={getCellValueWrapper}
                localizeUnit={localizeUnit}
                onImageClick={handleImageClick}
                onWarehouseStockQuantityClick={handleWarehouseStockQuantityClick}
              />
            </Box>

            {/* Mobile: Kartlar */}
            <Box sx={{ display: { xs: 'block', md: 'none' } }}>
              {hierarchicalItems.map((item) => (
                <InventoryMobileCard
                  key={item.id}
                  item={item}
                  visibleColumns={visibleColumns}
                  bulkActionMode={bulkActionMode}
                  isSelected={selectedItems.includes(item.id)}
                  isExpanded={expandedRows.has(item.id)}
                  onToggleExpand={handleToggleRow}
                  onSelect={handleSelectItem}
                  onEdit={(item) => {
                    setSelectedItem(item);
                    setIsEditModalOpen(true);
                  }}
                  onDelete={(item) => {
                    setSelectedItem(item);
                    setIsDeleteModalOpen(true);
                  }}
                  onViewDetails={(item) => {
                    setSelectedItem(item);
                    setIsDetailModalOpen(true);
                  }}
                  onStockMovement={(item) => {
                    setSelectedItem(item);
                    setSelectedField('quantity');
                    setIsUpdateModalOpen(true);
                  }}
                  onStockMovementsReport={(item) => {
                    navigate('/accounting/inventory/movements', { 
                      state: { 
                        searchTerm: item.name 
                      } 
                    });
                  }}
                  onPriceClick={(item) => {
                    // Satış Fiyatı güncelleme modalını aç
                    setSelectedItem(item);
                    setSelectedField('salePrice');
                    setUpdateValue(item.salePrice);
                    setUpdateListPrice((item as any).listPrice || 0);
                    setIsUpdateModalOpen(true);
                  }}
                  onAction={(item, action) => {
                    if (action === 'imageLibrary') {
                      handleOpenImageLibraryModal(item);
                    } else if (action === 'toggleEcommerce') {
                      handleToggleEcommerce(item);
                    } else if (action === 'toggleQRMenu') {
                      setSelectedItem(item);
                      handleToggleQRMenu();
                    } else if (action === 'toggleKiosk') {
                      setSelectedItem(item);
                      handleToggleKiosk();
                    } else if (action === 'sendToN11') {
                      setSelectedItem(item);
                      handleSendToN11();
                    }
                  }}
                  level={item.parentItemId || item.mainItemId ? 1 : 0}
                />
              ))}

              {/* Mobil: Pagination */}
              <Box sx={{ mt: 3, mb: 2 }}>
                <TablePagination
                  component="div"
                  count={totalCount}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[25, 50, 100]}
                  labelRowsPerPage=""
                  labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
                  sx={{
                    '.MuiTablePagination-toolbar': {
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      minHeight: 52,
                      px: 1
                    },
                    '.MuiTablePagination-selectLabel': {
                      display: 'none'
                    },
                    '.MuiTablePagination-displayedRows': {
                      margin: 0,
                      fontSize: '0.875rem'
                    },
                    '.MuiTablePagination-select': {
                      fontSize: '0.875rem'
                    }
                  }}
                />
              </Box>

              {/* Mobil: Switches ve Filtreler */}
              <Paper 
                elevation={2} 
                sx={{ 
                  p: 2, 
                  mt: 2,
                  borderRadius: 2,
                  bgcolor: 'grey.50'
                }}
              >
                <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 2 }}>
                  Filtreler
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideEmptyStock}
                        onChange={(e) => {
                          setHideEmptyStock(e.target.checked);
                          localStorage.setItem(HIDE_EMPTY_STOCK_KEY, JSON.stringify(e.target.checked));
                        }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Stoğu olmayan gizle</Typography>}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideInactiveItems}
                        onChange={(e) => {
                          setHideInactiveItems(e.target.checked);
                          localStorage.setItem(HIDE_INACTIVE_ITEMS_KEY, JSON.stringify(e.target.checked));
                        }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Pasif gizle</Typography>}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={sortNewestFirst}
                        onChange={(e) => {
                          setSortNewestFirst(e.target.checked);
                          localStorage.setItem('INVENTORY_SORT_NEWEST_FIRST', JSON.stringify(e.target.checked));
                        }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Yeniden eskiye sırala</Typography>}
                  />
                  
                  <FormControlLabel
                    control={
                      <Switch
                        checked={hideZeroPrice}
                        onChange={(e) => {
                          setHideZeroPrice(e.target.checked);
                          localStorage.setItem('INVENTORY_HIDE_ZERO_PRICE', JSON.stringify(e.target.checked));
                        }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Fiyatı 0 gizle</Typography>}
                  />

                  <FormControlLabel
                    control={
                      <Switch
                        checked={showVariantChildren}
                        onChange={(e) => {
                          setShowVariantChildren(e.target.checked);
                          localStorage.setItem('INVENTORY_SHOW_VARIANT_CHILDREN', JSON.stringify(e.target.checked));
                        }}
                        size="small"
                      />
                    }
                    label={<Typography variant="body2">Alt varyantları da göster</Typography>}
                  />
                </Box>
              </Paper>
            </Box>
          </Box>
        )}
        
        {/* Sayfa altında ek boşluk */}
        <Box sx={{ height: '100px' }} />
      </Box>

      {/* ✅ Tüm Modals tek component'te */}
      <InventoryModals
        isAddModalOpen={isAddModalOpen}
        isEditModalOpen={isEditModalOpen}
        isQuickAddModalOpen={isQuickAddModalOpen}
        isCategoryModalOpen={isCategoryModalOpen}
        isProductFeatureModalOpen={isProductFeatureModalOpen}
        isBrandModalOpen={isBrandModalOpen}
        isWarehouseModalOpen={isWarehouseModalOpen}
        isTableSettingsModalOpen={isTableSettingsModalOpen}
        isImportModalOpen={isImportModalOpen}
        deleteDialogOpen={deleteDialogOpen}
        isUpdateModalOpen={isUpdateModalOpen}
        isDetailModalOpen={isDetailModalOpen}
        isImageLibraryModalOpen={isImageLibraryModalOpen}
        isN11UploadModalOpen={isN11UploadModalOpen}
        isN11CategoryModalOpen={isN11CategoryModalOpen}
        isN11LogModalOpen={isN11LogModalOpen}
        isShipmentTemplatesModalOpen={isShipmentTemplatesModalOpen}
        isBulkActionSelectionModalOpen={isBulkActionSelectionModalOpen}
        isOtherActionsModalOpen={isOtherActionsModalOpen}
        showDeleteAllDialog={showDeleteAllDialog}
        snackbarOpen={snackbarOpen}
        onCloseAddModal={handleCloseAddModal}
        onCloseEditModal={() => {
          setIsEditModalOpen(false);
          setSelectedItem(null);
          // Listeyi yenile
          handleRefreshInventory();
        }}
        onCloseQuickAddModal={handleCloseQuickAddModal}
        onCloseCategoryModal={handleCloseCategoryModal}
        onCloseProductFeatureModal={handleCloseProductFeatureModal}
        onCloseBrandModal={handleCloseBrandModal}
        onCloseWarehouseModal={handleCloseWarehouseModal}
        onCloseTableSettingsModal={handleCloseTableSettingsModal}
        onCloseImportModal={handleCloseImportModal}
        onCloseDeleteDialog={handleCloseDeleteDialog}
        onCloseUpdateModal={handleCloseUpdateModal}
        onCloseDetailModal={handleCloseDetailModal}
        onCloseImageLibraryModal={handleCloseImageLibraryModal}
        onCloseN11UploadModal={handleCloseN11UploadModal}
        onCloseN11CategoryModal={handleN11CategoryModalClose}
        onCloseSnackbar={handleCloseSnackbar}
        onMenuStockMovementsReport={handleMenuStockMovementsReport}
        selectedItem={selectedItem}
        selectedImageItem={selectedImageItem}
        selectedItemForN11={selectedItemForN11}
        selectedField={selectedField}
        selectedBranch={selectedBranch}
        selectedWarehouse={selectedWarehouse}
        categories={categories}
        brands={brands}
        warehouses={warehouses}
        tableColumns={tableColumns}
        companyId={companyId}
        inventoryItems={inventoryItems}
        updateValue={updateValue}
        updateStringValue={updateStringValue}
        updateListPrice={updateListPrice}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        syncWithN11={syncWithN11}
        n11IntegrationActive={n11IntegrationActive}
        isN11Syncing={isN11Syncing}
        n11SendingProduct={n11SendingProduct}
        snackbarMessage={snackbarMessage}
        snackbarSeverity={snackbarSeverity}
        deletingAll={deletingAll}
        deleteProgress={deleteProgress}
        deletedItemsCount={deletedItemsCount}
        totalItemsToDelete={totalItemsToDelete}
        refreshing={refreshing}
        loading={loading}
        onUpdateValueChange={setUpdateValue}
        onUpdateStringValueChange={setUpdateStringValue}
        onUpdateListPriceChange={setUpdateListPrice}
        onSyncWithN11Change={setSyncWithN11}
        onConfirmUpdate={handleUpdateValue}
        onDeleteItem={handleDeleteItem}
        onUpdateTableSettings={handleUpdateTableSettings}
        onItemUpdate={(updatedItem) => {
          setInventoryItems(prevItems => 
            prevItems.map(item => 
              item.id === updatedItem.id ? updatedItem : item
            )
          );
          // KALICI KURAL: Parent urun kontrolunde isMainItem degil,
          // parentItemId/isVariantParent alanlari esas alinir.
          if (updatedItem.isVariantParent === true || !updatedItem.parentItemId) {
            const childItems = inventoryItems.filter(item => item.parentItemId === updatedItem.id);
            if (childItems.length > 0) {
              handleUpdateMainItem(updatedItem, childItems);
            }
          }
        }}
        onSaveImages={handleSaveImages}
        onSaveN11Log={saveN11Log}
        onN11CategoryConfirm={handleN11CategoryConfirm}
        onConfirmDeleteAll={confirmDeleteAllItems}
        onCancelDeleteAll={cancelDeleteAllItems}
        bulkOps={bulkOps}
        selectedItems={selectedItems}
        onBulkDeleteConfirm={bulkHandlers.confirmBulkDelete}
        onBulkDeleteCancel={bulkHandlers.cancelBulkDelete}
        onBulkInactivateConfirm={bulkHandlers.confirmBulkInactivate}
        onBulkInactivateCancel={bulkHandlers.cancelBulkInactivate}
        onBulkPriceUpdateConfirm={bulkHandlers.confirmBulkPriceUpdate}
        onBulkPriceUpdateCancel={bulkHandlers.cancelBulkPriceUpdate}
        onBulkDeleteImagesConfirm={confirmBulkDeleteImages}
        onBulkDeleteImagesCancel={cancelBulkDeleteImages}
        onBulkEcommerceConfirm={bulkHandlers.confirmBulkEcommerceAction}
        onBulkEcommerceCancel={bulkHandlers.cancelBulkEcommerceAction}
        onBulkToggleQRMenuConfirm={confirmBulkToggleQRMenu}
        onBulkToggleQRMenuCancel={cancelBulkToggleQRMenu}
        onBulkToggleKioskConfirm={confirmBulkToggleKiosk}
        onBulkToggleKioskCancel={cancelBulkToggleKiosk}
        onBulkAssignWarehouseConfirm={confirmBulkAssignWarehouse}
        onBulkAssignWarehouseCancel={cancelBulkAssignWarehouse}
        onBulkUpdateVatRateConfirm={confirmBulkUpdateVatRate}
        onBulkUpdateVatRateCancel={cancelBulkUpdateVatRate}
        onBulkUpdateDepartmentNoConfirm={confirmBulkUpdateDepartmentNo}
        onBulkUpdateDepartmentNoCancel={cancelBulkUpdateDepartmentNo}
        onBulkUpdateVatAndDeptConfirm={confirmBulkUpdateVatAndDept}
        onBulkUpdateVatAndDeptCancel={cancelBulkUpdateVatAndDept}
        onSelectBulkAction={handleSelectBulkAction}
        onCloseBulkActionModal={() => setIsBulkActionSelectionModalOpen(false)}
        menuAnchorEl={menuAnchorEl}
        menuItemId={menuItemId}
        ecommerceStatuses={ecommerceStatuses}
        onMenuClose={handleMenuClose}
        onMenuDetails={handleMenuDetails}
        onMenuStockMovement={handleMenuStockMovement}
        onMenuEdit={handleMenuEdit}
        onMenuDelete={handleMenuDelete}
        onOpenForEcommerce={handleOpenForEcommerce}
        onSendToN11={handleSendToN11}
        onToggleQRMenu={handleToggleQRMenu}
        onToggleKiosk={handleToggleKiosk}
        onActivateItem={handleActivateItem}
        onOpenBranchPrice={handleOpenBranchPrice}
        onOpenRecipeModal={handleOpenRecipeModal}
        onOpenMenuModal={handleOpenProductMenuModal}
        onOpenVariantModal={handleOpenVariantManagementModal}
        anchorElFeatures={anchorElFeatures}
        onCloseFeaturesMenu={handleCloseFeaturesMenu}
        bulkActionMode={bulkActionMode}
        loadingExport={loadingExport}
        isMobile={isMobile}
        onOpenCategoryModal={handleOpenCategoryModal}
        onOpenProductFeatureModal={handleOpenProductFeatureModal}
        onOpenBrandModal={handleOpenBrandModal}
        onOpenWarehouseModal={handleOpenWarehouseModal}
        onStockMovementsReport={handleStockMovementsReport}
        onToggleBulkActionMode={toggleBulkActionMode}
        onNavigateToImageLibrary={() => navigate('/resim-kutuphanesi')}
        onOpenTableSettings={handleOpenTableSettingsModal}
        onOpenShipmentTemplates={() => setIsShipmentTemplatesModalOpen(true)}
        onExportToExcel={exportToExcel}
        onOpenImportModal={handleOpenImportModal}
        onOpenN11Upload={handleOpenN11UploadModal}
        onOpenN11Log={() => setIsN11LogModalOpen(true)}
        onOpenOtherActionsModal={() => setIsOtherActionsModalOpen(true)}
        onPriceChangeLogs={handlePriceChangeLogs}
        onOpenInventoryInfo={() => setIsInventoryInfoModalOpen(true)}
        onNavigateToImageLibraryFromOther={() => {
                  navigate('/resim-kutuphanesi');
                  setIsOtherActionsModalOpen(false);
                }}
        onOpenTableSettingsFromOther={() => {
                  handleOpenTableSettingsModal();
                  setIsOtherActionsModalOpen(false);
                }}
        onOpenShipmentTemplatesFromOther={() => {
                  setIsShipmentTemplatesModalOpen(true);
                  setIsOtherActionsModalOpen(false);
                }}
        onOpenN11LogFromOther={() => {
                  setIsN11LogModalOpen(true);
                  setIsOtherActionsModalOpen(false);
                }}
        onOpenN11UploadFromOther={() => {
                  handleOpenN11UploadModal();
                  setIsOtherActionsModalOpen(false);
                }}
        onRefreshInventory={async () => {
          await handleRefreshInventory();
                  setIsOtherActionsModalOpen(false);
                }}
        onCloseOtherActionsModal={() => setIsOtherActionsModalOpen(false)}
      />

      <Dialog
        open={isInventoryInfoModalOpen}
        onClose={() => setIsInventoryInfoModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1
          }}
        >
          <Typography variant="h6" fontWeight="bold">
            {t('stock.infoModal.title')}
          </Typography>
          <IconButton onClick={() => setIsInventoryInfoModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {t('stock.infoModal.description')}
            </Typography>
          </Alert>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('stock.infoModal.quickStartTitle')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            {t('stock.infoModal.item1')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            {t('stock.infoModal.item2')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('stock.infoModal.item3')}
          </Typography>

          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
            {t('stock.infoModal.dailyFlowTitle')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            {t('stock.infoModal.item4')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.75 }}>
            {t('stock.infoModal.item5')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('stock.infoModal.item6')}
          </Typography>

          <Alert severity="success" variant="outlined">
            <Typography variant="body2">
              {t('stock.infoModal.tip')}
            </Typography>
          </Alert>
        </DialogContent>
      </Dialog>

      {/* ✅ Şube Fiyatı Modalı */}
      <BranchPriceModal
        open={isBranchPriceModalOpen}
        onClose={() => {
          setIsBranchPriceModalOpen(false);
          setBranchPriceSelectedItem(null);
        }}
        item={branchPriceSelectedItem}
        branches={branches}
        companyId={companyId || ''}
        onSuccess={() => {
          // Listeyi yenile
          handleRefreshInventory();
        }}
      />

      {/* ✅ Basit Üretim Reçetesi Modalı */}
      <RecipeModal
        open={isRecipeModalOpen}
        onClose={() => {
          setIsRecipeModalOpen(false);
          setRecipeSelectedItem(null);
        }}
        item={recipeSelectedItem}
        inventoryItems={inventoryItems}
        companyId={companyId || ''}
        warehouses={warehouses}
        categories={categories}
        onSuccess={() => {
          handleRefreshInventory();
        }}
      />

      {/* ✅ Menü Tanımları Modalı */}
      <ProductMenuModal
        open={isProductMenuModalOpen}
        onClose={() => {
          setIsProductMenuModalOpen(false);
          setProductMenuSelectedItem(null);
        }}
        item={productMenuSelectedItem}
        inventoryItems={inventoryItems}
        companyId={companyId || ''}
        warehouses={warehouses}
        categories={categories}
        onSuccess={() => {
          handleRefreshInventory();
        }}
      />

      <VariantManagementModal
        open={isVariantManagementModalOpen}
        onClose={() => {
          setIsVariantManagementModalOpen(false);
          setVariantManagementSelectedItem(null);
        }}
        item={variantManagementSelectedItem}
        warehouses={warehouses}
        onCreated={() => {
          handleRefreshInventory();
        }}
      />

      {/* ✅ Barkod Okuma Modalı */}
      <BarcodeScanner
        open={isBarcodeScannerOpen}
        onClose={handleCloseBarcodeScanner}
        onBarcodeScanned={handleBarcodeScanned}
      />

      {/* ✅ YENİ: Resim Menüsü (Resmi Gör / Resmi Güncelle / Resmi Kaldır) */}
      <ImageActionMenu
        anchorEl={imageMenuAnchor}
        open={Boolean(imageMenuAnchor)}
        onClose={handleImageMenuClose}
        onView={handleViewImage}
        onUpdate={handleEditImage}
        onRemove={handleRemoveImageRequest}
        labels={{ view: 'Resmi Gör', update: 'Resmi Güncelle', remove: 'Resmi Kaldır' }}
      />

      {/* ✅ YENİ: Resim Kaldırma Onay Dialogu */}
      <ConfirmDialog
        open={isRemoveImageConfirmOpen}
        onClose={() => setIsRemoveImageConfirmOpen(false)}
        onConfirm={confirmRemoveImage}
        title="Resmi Kaldır"
        content={`"${selectedImageItem?.name || 'Ürün'}" için tüm resimler kaldırılacak. Devam etmek istiyor musunuz?`}
        confirmButtonText="Kaldır"
        cancelButtonText="İptal"
        isLoading={isRemovingImage}
      />

      {/* ✅ YENİ: Resim Görüntüleme Modalı */}
      <ImageViewModal
        open={isImageViewModalOpen}
        onClose={handleCloseImageViewModal}
        imageUrls={selectedImageItem?.imageUrl 
          ? selectedImageItem.imageUrl.split(',').map(url => url.trim()).filter(url => url)
          : []
        }
        itemName={selectedImageItem?.name || ''}
        onEditImages={handleEditImage}
      />
    </>
  );
};

export default Inventory; 